import { Injectable, OnDestroy } from '@angular/core';

import { ReplaySubject, Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppConstants } from '@unpispas/upp-defs';
import { CommitChange } from './model/base';

import { UserMode } from '@unpispas/upp-base';
import { identificationService } from '@unpispas/upp-base';
import { adhocService } from '@unpispas/upp-base';
import type { AdhocRequestCancel } from '@unpispas/upp-base';
import { httpService } from '@unpispas/upp-base';
import { stateService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { alertService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { clockService } from '@unpispas/upp-base';

import { StorageDDBB, StorageMeta, StorageData }  from './cache';
import { cacheService } from './cache';
import { wsconnectService } from './wsconnect';

import { User } from './model/objects/user';
import { QrCode } from './model/objects/qrcode';
import { QrScan } from './model/objects/qrscan';
import { Place } from './model/objects/place';
import { Ticketbai } from './model/objects/ticketbai';

/**
 * Represents the set of triggers used to configure and update a server connection.
 * 
 * Triggers define the conditions under which the connection is active and synchronized.
 * They allow the server to associate the connection with specific entities, such as 
 * a session, QR code, user, or place.
 */
interface UpdateTriggers {

    /**
     * The session identifier associated with the connection.
     * This value is mandatory and uniquely identifies the session.
     */    
    SESSION: string,

    /**
     * Global QR scan (guest table) identifier for wake. Unique across places.
     * If no scan is associated, this value is `null`.
     */
    QRSCAN: string | null,

    /**
     * The user identifier associated with the connection, if applicable.
     * If no user is associated, this value is `null`.
     */
    USER: string | null,

    /**
     * The place identifier associated with the connection, if applicable.
     * If no place is associated, this value is `null`.
     */
    PLACE: string | null
}

type LoadTarget = 'SESSION' | 'PLACE' | 'QRCODE';

/**
 * Represents the payload sent to the server for a synchronization request.
 */
interface UpdateRequest {
    /**
     * The access mode for the session.
     * Defines the permissions or roles of the user in the current session.
     */    
    access: UserMode;

    /**
     * The timestamp of the last successful response from the server.
     * If this is the first request in a session, the value should be `0`.
     */    
    timestamp: number;

    /**
     * The target currently being loaded or synchronized.
     * Specifies the entity (e.g., SESSION, PLACE, QRCODE) associated with this request.
     */
    target: LoadTarget;
}

/**
 * Represents the response received from the server after a synchronization request.
 */
interface UpdateRespose {

    /**
     * The error code indicating the result of the operation.
     * A value of `0` typically represents success, while other values indicate specific errors.
     */    
    errorcode: number;

    /**
     * The current timestamp for the next synchronization operation.
     * Used to track the server's state and synchronize updates incrementally.
     */
    timestamp: number;

    /**
     * The server's time reference for updates.
     * This value helps adjust the local client clock to align with the server's timing.
     */    
    reference: number;

    /**
     * The target currently loaded or synchronized.
     * Mirrors the target specified in the request, representing the entity being synchronized.
     */
    target: LoadTarget;

    /**
     * Indicates whether the committed data has been updated.
     * Even on failure, this flag may be set to `true` if some changes were partially applied.
     */
    updated: boolean;

    /**
     * Indicates whether the user's session has expired.
     * If `true`, the session needs to be reauthenticated before further requests.
     */
    expired: boolean;

    /**
     * A list of changes in the server data.
     * Contains the updated entries resulting from the synchronization operation.
     */
    updates: Array <any>;
}

type ConnectionStatus = 'RUNNING' | 'STOPPED' | 'EXPIRED';

/**
 * Manages the lifecycle of a client-server connection for data synchronization.
 * Includes methods for creating, updating, and releasing the connection, 
 * as well as controlling data refresh cycles.
 */
class Connection {
    private _doCancelUrl = 'connection/cancel';
    private _doCreateUrl = 'connection/create';
    private _doDeleteUrl = 'connection/delete';
    private _doUpdateUrl = 'connection/update';

    /** Bound wait for connection/* when the UI awaits setup before a commit. */
    private static readonly _CONN_TIMEOUT_MS = 12000;

    /**
     * Creates a new `Connection` instance.
     * @param sync - The `syncService` instance responsible for synchronization.
     */    
    constructor(private sync: syncService){
        // nothing to do
    }

    /**
     * Gets the `adhocService` instance from the provided `syncService`.
     */    
    private get adhoc(): adhocService {
        return this.sync.adhoc;
    }

    /**
     * Gets the `stateService` instance from the provided `syncService`.
     */    
    private get state(): stateService {
        return this.sync.state;
    }

    /**
     * Gets the `Syncronizator` instance from the provided `syncService`.
     */    
    private get syncr(): Syncronizator {
        return this.sync.synchonizer;
    }

    private _status: ConnectionStatus  = 'STOPPED';

    /**
     * Gets the current connection status.
     */    
    get status(): ConnectionStatus{
        return this._status;
    }

    /**
     * Sets the current connection status.
     */   
    set status(value: ConnectionStatus){
        this._status = value;
    }

    private _triggers: UpdateTriggers | null = null

    /**
     * Gets the current update triggers for the connection.
     */    
    get triggers(){
        return this._triggers;
    }

    /***************************************/
    /* CONNECTION CONTROL                  */
    /***************************************/

    private _reconnect_timeout: ReturnType<typeof setTimeout> | null = null;
    private _reconnect_attempt = 0;
    private _reconnect_errortype: 'NONE' | 'TRANSPORT' | 'SERVER' = 'NONE';

    /**
     * Creates a new connection and initializes data refresh.
     * When `allowDegraded` is true and create fails only by transport, keeps the session
     * usable from cache and schedules reconnect (RN-SYNC-15) instead of throwing.
     * @param options.allowDegraded - Allow offline start when session cache was loaded.
     * @returns A promise resolving to `true` if the connection is created or degraded start is accepted.
     */    
    async CreateConnection(options?: { allowDegraded?: boolean }): Promise <boolean> {
        if (this.syncr.IsUnloading){
            return false;
        }

        const _session = this.state.session;
        if (_session == null){
            throw new Error("[SYNC] Session must be configured!")
        }

        const _buildTriggers = (): UpdateTriggers => ({
            SESSION: _session,
            QRSCAN: this.sync.qrscan?.objid ?? null,
            USER: this.sync.user?.objid ?? null,
            PLACE: this.sync.place?.objid
                ?? this.sync.qrscan?.place?.objid
                ?? null,
        });

        // SetPlace/SetUser may run while create is in flight; realign before refresh.
        if (await this.configureTriggers(_buildTriggers())){
            this._ClearReconnect();
            if (this._triggers && this.status == 'RUNNING'){
                const _expected = _buildTriggers();
                if (JSON.stringify(this._triggers) != JSON.stringify(_expected)){
                    await this.UpdateConnection(_expected);
                }
            }
            await this.initializeRefresh();
        }
        else {
            if (this.status == 'EXPIRED'){
                console.warn("[SYNC] Cannot configure connection. Session is expired.")
            }
            else if (options?.allowDegraded && this._reconnect_errortype == 'TRANSPORT'){
                console.warn("[SYNC] Starting degraded: no server connection, using session cache");
                this._ScheduleReconnect();
            }
            else {
                throw new Error("[SYNC] Cannot configure connection. Triggers not set")
            }
        }

        return true;
    }

    /**
     * Refreshes the connection by reconfiguring the update triggers.
     * @returns A promise resolving to `true` if the refresh is successful, `false` otherwise.
     */    
    async RefreshConnection(): Promise <boolean> {
        if (this.syncr.IsUnloading){
            return false;
        }

        return await this.configureTriggers(this._triggers);
    }

    /**
     * Stops refresh/reconnect for browser pagehide without a final sync.
     * Local only: do **not** call `connection/cancel` here — that endpoint runs
     * `check_session` / `validate_session`, which can revive DE→AC in the same
     * instant as `connection/unload` release (icon stays online after tab close).
     * Unload already `connection_delete`s the wait entry.
     */
    HaltForPageUnload(): void {
        this._ClearReconnect();
        if (this._refresh_timeout){
            clearTimeout(this._refresh_timeout);
            this._refresh_timeout = null;
        }

        this._status = 'STOPPED';
    }

    /**
     * Releases the current connection and finalizes the refresh cycle.
     * @returns A promise resolving to `true` if the release is successful, `false` otherwise.
     */    
    async ReleaseConnection() : Promise <boolean> {
        this._ClearReconnect();
        if (await this.configureTriggers(null)){
            await this.finalizeRefresh();
        }

        return true;
    }

    /**
     * Clears the deferred reconnect timer used after a degraded start.
     */
    private _ClearReconnect(): void {
        if (this._reconnect_timeout){
            clearTimeout(this._reconnect_timeout);
            this._reconnect_timeout = null;
        }
        this._reconnect_attempt = 0;
    }

    /**
     * Schedules a later attempt to create the server connection after degraded start.
     */
    private _ScheduleReconnect(): void {
        if (this._reconnect_timeout){
            return;
        }

        const _delay = Math.min(60000, 2000 * Math.pow(2, Math.min(this._reconnect_attempt, 5)));
        this._reconnect_timeout = setTimeout(() => {
            this._reconnect_timeout = null;
            void this._TryReconnect();
        }, _delay);
    }

    /**
     * Retries `connection/create` until the connection is RUNNING or the session expires.
     */
    private async _TryReconnect(): Promise <void> {
        if ((this.status == 'RUNNING') || (this.status == 'EXPIRED')){
            return;
        }
        if (!this.state.session){
            return;
        }

        this._reconnect_attempt++;
        const _triggers: UpdateTriggers = {
            SESSION: this.state.session,
            QRSCAN: this.sync.qrscan?.objid ?? null,
            USER: this.sync.user?.objid ?? null,
            PLACE: this.sync.place?.objid
                ?? this.sync.qrscan?.place?.objid
                ?? null,
        };

        if (await this.configureTriggers(_triggers)){
            this._ClearReconnect();
            await this.initializeRefresh();
            return;
        }

        // Status may become EXPIRED during configureTriggers; next tick no-ops if so.
        this._ScheduleReconnect();
    }

    /***************************************/
    /* ENTRY POINT                         */
    /***************************************/

    /**
     * Updates the connection to associate a user.
     * @param user - The user to associate, or `null` to remove the user.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */    
    async SetUser(user: User | null): Promise <boolean> {
        if (!this._triggers){
            return true;    // nothing to update
        }

        const _triggers: UpdateTriggers = Object.assign({}, this._triggers);

        _triggers.QRSCAN = null;
        _triggers.USER = user ? user.objid : null;
        _triggers.PLACE = null;

        return await this.UpdateConnection(_triggers);
    }

    /**
     * Updates the connection to associate a place.
     * @param place - The place to associate, or `null` to remove the place.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */    
    async SetPlace(place: Place | null): Promise <boolean> {
        if (!this._triggers){
            return true;    // nothing to update
        }

        const _triggers: UpdateTriggers = Object.assign({}, this._triggers);

        _triggers.QRSCAN = null;
        _triggers.USER = place?.user?.objid || null;
        _triggers.PLACE = place?.objid || null;

        return await this.UpdateConnection(_triggers);        
    }

    /**
     * Updates the connection to associate a QR scan (guest table).
     * Registers PLACE + QRSCAN wake triggers (global scan id, unique across places).
     * @param qrscan - The scan to associate, or `null` to remove it.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */
    async SetQrScan(qrscan: QrScan | null): Promise <boolean> {
        if (!this._triggers){
            return true;
        }

        const _triggers: UpdateTriggers = Object.assign({}, this._triggers);
        _triggers.QRSCAN = qrscan?.objid ?? null;
        _triggers.PLACE = qrscan?.place?.objid ?? null;
        return await this.UpdateConnection(_triggers);
    }

    /**
     * Updates the connection to associate a QR code.
     * Guest wake stays on QRSCAN (from the code's scan or the scan already bound).
     * @param qrcode - The QR code to associate, or `null` to remove the QR code.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */
    async SetQrCode(qrcode: QrCode | null): Promise <boolean> {
        if (!this._triggers){
            return false;
        }

        const _scan = qrcode
            ? (qrcode.qrscan ?? this.sync.qrscan ?? null)
            : null;

        const _triggers: UpdateTriggers = Object.assign({}, this._triggers);

        _triggers.QRSCAN = _scan?.objid ?? null;
        _triggers.USER = null;
        _triggers.PLACE = qrcode
            ? (qrcode.place?.objid
                ?? _scan?.place?.objid
                ?? this.sync.qrscan?.place?.objid
                ?? null)
            : null;

        return await this.UpdateConnection(_triggers);        
    }

    /**
     * Cancels the current data refresh operation.
     * @returns A promise resolving to `true` if the cancellation is successful, `false` otherwise.
     */    
    async CancelRefresh(): Promise <boolean> {
        try {
            const data = await this.adhoc.DoRequest(this._doCancelUrl, null, null, false, null, null, null, Connection._CONN_TIMEOUT_MS);
            const _success = (data['errorcode'] == 0);
            if (!_success){
                console.error("[CONNECTED] Error [" + data['errorcode'] + "] sending cancelation");
            }
            return _success;
        } 
        catch (error: any) {
            return this._throwerror(this._doCancelUrl, error);
        }
    }

    /******************************/
    /* REFRESH CONTROL            */
    /******************************/

    /**
     * Initializes the data refresh process
     */    
    private async initializeRefresh(){
        if (this._status == 'EXPIRED'){
            this._status = 'STOPPED';
        }

        console.info("[INITIALIZE] Connected to data service");
        await this.resumeRefresh();
    }

    /**
     * Finalizes the data refresh process and runs a final synchronization.
     */    
    private async finalizeRefresh(){
        const _expired = (this._status == 'EXPIRED');
        const _started = (this._status == 'RUNNING');

        await this.pauseRefresh(false);
        if (!_expired && _started){
            await this.syncr.DoRefresh(false, false);        
        }

        this._status ='STOPPED';
    }

    /**
     * Pauses the data refresh process.
     */    
    private async pauseRefresh(stop = true){
        if (this._refresh_timeout){
            clearTimeout(this._refresh_timeout);
            this._refresh_timeout = null;
        }

        const _expired = (this._status == 'EXPIRED');
        if (!_expired){
            if (stop){
                this._status = 'STOPPED';
            }

            await this.syncr.CancelRefresh();    
        }
    }

    /**
     * Resumes the data refresh process if the connection is active.
     */    
    private async resumeRefresh(){
        const _expired = (this._status == 'EXPIRED');
        if (!_expired){
            if (this._status == 'RUNNING'){
                console.warn("[INITIALIZE] Already running!");
                return;
            }
    
            this._status = 'RUNNING';
            this.repeatRefresh();        
        }
    }

    private _refresh_timeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Initiates periodic refresh cycles while the connection is active.
     * PTF uses MainRefresh×4 (modest terminals); forced refresh / flush unchanged.
     */    
    private repeatRefresh(){
        if (this._refresh_timeout){
            clearTimeout(this._refresh_timeout);
            this._refresh_timeout = null;
        }

        this._refresh_timeout = setTimeout(() => {
            if (this._status == 'RUNNING') {
                this.syncr.DoRefresh(false, false).then(
                () => {
                    this.repeatRefresh();
                });        
            }
            else {
                console.info("[SYNC] Skip refresh (not running)")
            }
        }, AppConstants.MainRefresh * (this.state.WorkMode === 'PTF' ? 4 : 1));
    }

    /******************************/
    /* UPDATE CONTROL             */
    /******************************/

    /**
     * Configures or clears update triggers for the connection.
     * 
     * When triggers are provided, this method sends a request to the server to create
     * or update the connection. If triggers are `null`, it sends a request to delete
     * the connection.
     * 
     * @param triggers - The `UpdateTriggers` object containing configuration for the connection, or `null` to clear triggers.
     * @returns A promise resolving to `true` if the operation is successful, `false` otherwise.
     * @throws If triggers are `null` and the connection is not running, an error is thrown.
     */    
    private async configureTriggers(triggers: UpdateTriggers | null) : Promise <boolean> {
        if ((this._triggers == triggers) && (triggers != null)){
            try {
                const data = await this.adhoc.DoRequest(this._doUpdateUrl, null, triggers, false, null, null, null, Connection._CONN_TIMEOUT_MS);
                if (data['errorcode'] == 0){
                    this._reconnect_errortype = 'NONE';
                    console.info("[CONNECTED] Connected to server (id: " + data['connection'] + ")");
                }
                else {
                    this._reconnect_errortype = 'SERVER';
                    if ((data['errorcode'] == 2) || (data['errorcode'] == 3)){
                        this.sync.OnSessionExpired();
                    }
                    else {
                        console.error("[CONNECTED] Error [" + data['errorcode'] + "] updating connection");
                    }
                }

                return (data['errorcode'] == 0);
            } 
            catch (error: any) {
                this._reconnect_errortype = 'TRANSPORT';
                return this._throwerror(this._doUpdateUrl, error);
            }
        }

        if (triggers){
            try {
                const data = await this.adhoc.DoRequest(this._doCreateUrl, null, triggers, false, null, null, null, Connection._CONN_TIMEOUT_MS);
                if (data['errorcode'] == 0){
                    this._reconnect_errortype = 'NONE';
                    this._triggers = triggers;
                    console.info("[CONNECTED] Connected to server (id: " + data['connection'] + ")");
                }
                else {
                    this._reconnect_errortype = 'SERVER';
                    if ((data['errorcode'] == 2) || (data['errorcode'] == 3)){
                        this.sync.OnSessionExpired();
                    }
                    else {
                        console.error("[CONNECTED] Error [" + data['errorcode'] + "] creating connection");
                    }
                }

                return (data['errorcode'] == 0);
            } 
            catch (error: any) {
                this._reconnect_errortype = 'TRANSPORT';
                return this._throwerror(this._doCreateUrl, error);
            }
        }
        else {
            if ((this.status == 'STOPPED') && (this._triggers == null) && (triggers == null)){
                return true;
            }

            // if we havent started, we need some triggers
            if (!(['RUNNING', 'EXPIRED'].includes(this.status))){
                throw new Error("[SYNC] update triggers are not set");
            }

            try {
                const data = await this.adhoc.DoRequest(this._doDeleteUrl, null, null, false, null, null, null, Connection._CONN_TIMEOUT_MS)
                if (data){
                    if ((data['errorcode'] == 0) || (data['errorcode'] == 2)) {
                        this._reconnect_errortype = 'NONE';
                        this._triggers = null;
                        console.info("[CONNECTED] Disconnected from service")
                        return true;
                    }
                    else {
                        this._reconnect_errortype = 'SERVER';
                        console.error("[CONNECTED] Error [" + data['errorcode'] + "] deleting connection");
                        return false;
                    }
                }

                this._reconnect_errortype = 'SERVER';
                return false;   // no data?
            }
            catch (error: any) {
                this._reconnect_errortype = 'TRANSPORT';
                return this._throwerror(this._doDeleteUrl, error);
            }
        }
    }

    /**
     * Updates the connection configuration with new triggers or releases the connection.
     * 
     * This method compares the new triggers with the current triggers. If they differ,
     * it sends a request to update the configuration on the server. If the triggers
     * are `null`, the connection is released.
     * 
     * @param triggers - The new `UpdateTriggers` object, or `null` to release the connection.
     * @returns A promise resolving to `true` if the operation is successful, `false` otherwise.
     */    
    private async UpdateConnection(triggers: UpdateTriggers | null): Promise <boolean> {
        if (this.status != 'RUNNING'){
            throw new Error("[CONNECTED] Cannot update connection if status is '" + this.status + "'")
        }

        else {  // status == 'RUNNING'
            if (triggers && triggers.SESSION){
                const _change = (JSON.stringify(this._triggers) != JSON.stringify(triggers));
                if (_change){
                    try {
                        const data = await this.adhoc.DoRequest(this._doUpdateUrl, null, triggers, false, null, null, null, Connection._CONN_TIMEOUT_MS);
                        if (data){
                            if (data['errorcode'] == 0){
                                this._triggers = triggers;
                                console.info("[CONNECTED] Server configuration updated..");
                                return true;                                
                            }
                            else {
                                console.error("[CONNECTED] Error [" + data['errorcode'] + "] updating service");
                                return false;
                            }
                        }

                        return false;   // no data ?
                    }
                    catch(error: any){
                        return this._throwerror(this._doUpdateUrl, error);
                    }
                }
                else {
                    return true;    // no changes
                }
            }
            else {
                return await this.ReleaseConnection();
            }
        }
    }

    /******************************/
    /* PRIVATE METHODS            */
    /******************************/

    /**
     * Logs error messages for failed HTTP requests.
     * @param url - The URL of the request.
     * @param error - Error details.
     * @returns `false` indicating failure.
     */    
    private _throwerror(url: string, error: any): boolean {
        if (error && error.status) {
            console.error("[REQUEST] Error [" + error.status + "] in http request '" + url + "'");
        } 
        else {
            console.error("[REQUEST] Generic error in http request '" + url + "'");
        }            

        return false;
    }
}

/**
 * Represents a queue for managing pending changes and updates in a synchronization service.
 * The queue handles priorities, processing order, and progress tracking for updates.
 */
class PendingQueue {
    private _pending: Array <any> = [];
    private _progress = [ 0, 0 ];
    private _stage = 0;

    private _refreshStage = new Subject<number> ();
    /** Observable for tracking refresh stages. */
    public OnRefreshStage = this._refreshStage.asObservable();

    private _refreshPercent = new Subject<number> ();
    /** Observable for tracking the percentage of refresh completion. */    
    public OnRefreshPercent = this._refreshPercent.asObservable();

    private _onChange = new Subject<any>();     // change recovered from server
    /** Observable for changes received from the server. */
    public OnChange = this._onChange.asObservable();

    /**
     * Creates a new `PendingQueue` instance.
     * @param sync - The synchronization service associated with this queue.
     */    
    constructor(private sync: syncService){
        // nothing to do
    }

    /**
     * Cleans up resources and subscriptions when the queue is destroyed.
     */    
    OnDestroy(){
        this.Reset();

        if (this._onblock_subscription){
            this._onblock_subscription.unsubscribe();
            this._onblock_subscription = null;
        }
    }

    /**
     * Provides access to the clock service.
     */    
    private get clock(): clockService {
        return this.sync.clock;
    }

    /**
     * Provides access to the state service.
     */    
    private get state(): stateService {
        return this.sync.state;
    }

    /*********************************/
    /* LOADING STATUS                */
    /*********************************/

    /**
     * Gets the current stage of the queue.
     */    
    get Stage(){
        return this._stage;
    }

    /**
     * Sets the current stage of the queue. Resets the queue if the stage is set to 0.
     */    
    set Stage(value){
        if (value == 0) {
            this.Reset();
        }

        const _next = Math.min(9, value);
        if (_next > this._stage){
            this._stage = _next;
            console.info(`[SYNC] stage=${this._stage} +${Math.round(performance.now())}ms`);

            this._refreshStage.next(this.Stage);
            setTimeout(() => {
                this.NotifyPercent();
            }, 0);
        }
    }

    /**
     * Gets the number of pending items in the queue.
     */    
    get length(){
        return this._pending.length;
    }

    /**
     * Resets the queue, clearing all pending items and resetting progress and stage.
     */    
    Reset(){
        if (this._process_timeout){
            clearTimeout(this._process_timeout);
            this._process_timeout = null;
        }

        this._progress[0] = 0;
        this._progress[1] = 0;

        this._stage = 0;
        this._pending = [];

        this._refreshStage.next(this.Stage);
    }

    /**
     * Notifies observers of the current refresh percentage.
     */    
    private NotifyPercent(){
        const percent: number = ((stage: number, current: number, total: number) => {
            if (stage === 0) {
                return 0;   // not started
            }
    
            if (stage === 9) {
                return 1;   // completed
            }
    
            return total === 0 ? 0 : Math.floor((current / total) * 100) / 100;
        })(this.Stage, this._progress[0], this._progress[1]);

        this._refreshPercent.next(percent);
    }

    /*********************************/
    /* QUEUE MANAGEMENT              */
    /*********************************/

    /**
     * Merges two change objects, resolving conflicts by using the most recently updated version.
     * @param chng1 - The first change object.
     * @param chng2 - The second change object.
     * @returns A merged change object.
     * @throws If the `updated` field is not in a valid date format.
     */    
    private _MergeChange(chng1: any, chng2: any): any {
        try {
            const updated1 = new Date(chng1['updated'].replace(' ', 'T') + '.000Z');
            const updated2 = new Date(chng2['updated'].replace(' ', 'T') + '.000Z');
    
            const source = updated1.getTime() > updated2.getTime() ? chng1 : chng2;
            const target = updated1.getTime() > updated2.getTime() ? chng2 : chng1;
    
            return { ...target, ...source }; // Merge with spread syntax
        } 
        catch {
            throw new Error("[SYNC] Invalid date format in received change");
        }
    }

    /**
     * Adds items to the queue and updates progress tracking.
     * @param _items - The items to enqueue.
     */    
    private Enqueue(_items: Array <any>){
        this._progress[1] += _items.length;
    }

    /**
     * Removes an item from the queue and processes it.
     * @param _item - The item to dequeue.
     */    
    private Dequeue(_item: any){
        const _pcnt0 = this._progress[1] === 0 ? 0 : Math.floor((this._progress[0] / this._progress[1]) * 100) / 100;
        this._progress[0]++;
        const _pcnt1 = this._progress[1] === 0 ? 0 : Math.floor((this._progress[0] / this._progress[1]) * 100) / 100;

        this._onChange.next(_item);

        this.Stage = parseInt(_item['__priority']);
        if (_pcnt0 != _pcnt1){
            this.NotifyPercent();
        }
    }

    /**
     * Merges and enqueues a list of updates, processing them in priority order.
     * @param _updates - The updates to enqueue.
     * @param _synchronous - Whether to process updates synchronously.
     */    
    async Concat(_updates: Array <any>, _synchronous: boolean){
        await Promise.resolve();   // release the event loop

        function ChangeKey(change: any){
            if (!('table' in change) || !('objid' in change)){
                throw new Error("[SYNC] Invalid update item received (missing fields)")
            }
    
            if (!change['table'] || !change['objid']){
                throw new Error("[SYNC] Invalid update item received (missing index values)")
            }
    
            return change['table'] + '@' + change['objid'];
        }

        if (_updates.length > 0){
            const _brief = [];    // only for debugging purposes

            // set the priority for all updates
            for (const _update of _updates){
                _update['__priority'] = this._priority(_update);

                // reprioritize inserted entries (in range [-9 .. -1])
                if (_update['_actn'] == 'do_insert'){
                    _update['__priority'] -= 10.0;
                }     
                
                // add to the updates brief report
                if (!(_update['table'] in _brief)){
                    _brief[_update['table']] = 0;
                }
                _brief[_update['table']]++;                
            }
    
            _updates.sort((a, b) => {
                return (a['__priority'] - b['__priority']);
            });

            // merge the udpates in the pending list
            if (this._pending.length > 0){
                const _pndmap = new Map();
                for(const _pending of this._pending){
                    _pndmap.set(ChangeKey(_pending), _pending);
                }
    
                for(let _idx = _updates.length - 1; _idx >= 0; _idx--){
                    const _update = _updates[_idx];
                    const _pnditm = _pndmap.get(ChangeKey(_update));

                    let _assign = null;   
                    if (_pnditm){
                        _assign = this._MergeChange(_update, _pnditm);      // merge this entry;    
                        _pndmap.set(ChangeKey(_update), _assign);     // replace this entry   
                        _updates.splice(_idx, 1);                           // remove from updates
                    }
                }
    
                this._pending = [..._pndmap.values()];
            }

            // enqueue all other updates (not in pending list)
            this.Enqueue(_updates);

            // release the synchronous entries      
            if (_updates.length > 0){
                this.clock.ClockEnable('synchronous', (this.Stage > 6));
                while(_updates.length > 0){
                    if ((!_synchronous) && (this.Stage > 3)) {
                        break;
                    }

                    this.Dequeue(_updates.shift());
                }
                this.clock.ClockEnable('synchronous', true);
            }
            
            if (_updates.length > 0){
                this._pending = this._pending.concat(_updates);
                this._pending.sort((a, b) => {
                    return (a['__priority'] - b['__priority']);
                });
            }
        }

        this.ProcessQueue();
    }

    /*********************************/
    /* PROCESS QUEUE                 */
    /*********************************/

    private _blockProcessed = new Subject <void> ();
    private OnBlockProcessed = this._blockProcessed.asObservable();

    private _onblock_subscription: Subscription | null = null;
    private _process_timeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Processes the queue in stages, prioritizing blocks of updates for efficiency.
     */    
    private _ProcessQueue(){
        let _stage = this.Stage;
        if (this._pending.length > 0){
            _stage = this._pending[0]['__priority'];
        }

        const _prioritize = (_stage < 6);

        let _todo = Math.min(this._pending.length, _prioritize ? 5000 : 500);
        this.clock.ClockEnable('queue', (_todo == 0) || !_prioritize);

        const _starttime = performance.now();  // max 50ms execution inside loop
        while ((_todo-- > 0) && ((performance.now() - _starttime) < 50)){
            const _oldstage = this.Stage;
            this.Dequeue(this._pending.shift());

            if (_oldstage != this.Stage){
                break;  // can change the max evaluation size
            }
        }
        
        this.clock.ClockEnable('queue', true);

        if ((this.Stage > 0) && (this._pending.length == 0)){
            this.Stage = 9;     // ensure that stage 9 is reached
        }

        // trigger block processed to continue with next block
        if (this._pending.length > 0){
            this._process_timeout = setTimeout(() => {
                this._blockProcessed.next();
            }, _prioritize ? 0 : 50);
        }
        else {  // no more items, remove the subscription
            if (this._onblock_subscription != null){
                this._onblock_subscription.unsubscribe();
                this._onblock_subscription = null;
            }
        }
    }

    /**
     * Starts processing the queue if it's not already being processed.
     */    
    private ProcessQueue(){
        if (this._process_timeout == null){
            if (this._onblock_subscription != null){
                this._onblock_subscription.unsubscribe();
                this._onblock_subscription = null;
            }
        }

        if (this._onblock_subscription == null){
            this._onblock_subscription = this.OnBlockProcessed.subscribe(
            () => {
                this._ProcessQueue();
            });
    
            this._blockProcessed.next();
        }
    }

    /*********************************/
    /* PRIORITY HANDLING             */
    /*********************************/

    /**
     * Determines the priority of a given update based on its table and context.
     * @param _update - The update to prioritize.
     * @returns A numeric priority value.
     */    
    private _priority(_update: any){
        // update synchronously
        switch(_update['table']){
            case 'SESSION': 
                if (this.state.session == _update['id']){
                    return 1.1;
                }
                break;

            case 'FCM': return 1.2;

            case 'USER': return 1.3;
            case 'STAFF': return 1.4;

            case 'PLACE': 
                if (!this.sync.place){
                    return 1.5;
                }
                break;
        }    

        // instances without graphical component
        switch(_update['table']){
            case 'ADDRESS': return 2.1;
            case 'I18NVALUE': return 2.1;
            case 'STRIPE': return 2.1;
            case 'RASPPI': return 2.1;
            case 'POSDEVICE': return 2.1;
            case 'TILL': return 2.15;
            case 'PLACEOPT': return 2.2;
            case 'DEVICEOPT': return 2.21;
            case 'PLACELINK': return 2.2;
            case 'PRICEZONE': return 2.22;
            case 'PRICETIME': return 2.23;
            case 'INVOICEBUSINESS': return 2.4;
            case 'ACCOUNTINVOICE': return 2.3;
            case 'PAYACCOUNT': return 2.4;
            case 'TILLCHANGE': return 2.5;
            case 'AUDITTILL': return 2.6;
            case 'AUDIT': return 2.7;
        }

        // Floor plan before till/audit/catalog (RN-SYNC-30): after PLACE 1.5, before any 2.x.
        switch(_update['table']){
            case 'DRAWITEM':
                return 1.81;

            case 'FLOORAREA':
            case 'RASPPICONNECT':
                return 1.82;
    
            case 'QRCODE':
                if (_update.status != 'DE'){
                    return 1.83;
                }
                break;

            case 'QRSCAN':
            case 'PRINTER':
            case 'SCALE':
            case 'CODESCAN':
            case 'PINPAD':
                if (_update.status != 'DE'){
                    return 3.3;
                }
                break;
    
            case 'OFFERPRODUCTOPT':
            case 'DISCOUNTPRODUCT':
            case 'EXTRAPRODUCTOPT':
            case 'PRINTERPRODUCTOPT':
            case 'PRODUCTPRICE':
            case 'OPTIONPRICE':
                    return 3.4;

            case 'PRODUCTOPT':
            case 'OFFERPRODUCT': 
            case 'OFFERPERIOD': 
            case 'DISCOUNTPERIOD':
            case 'EXTRATABLE': 
            case 'EXTRAPRODUCT':
            case 'EXTRAPERIOD':
            case 'PRINTERPRODUCT': 
                return 3.5;

            case 'CATEGORYDEP':
            case 'PRESELECTOPT':
                return 3.6;
    
            case 'CATEGORY':
            case 'PRESELECT':
                return 3.7;

            case 'PRODUCT':
            case 'OFFER':
            case 'EXTRA':
            case 'DISCOUNT':
                return 3.8;
        }

        // product families
        switch(_update['table']){
            case 'FAMILYPERIOD':
            case 'FAMILYPRODUCT':
                return 4.1;

            case 'FAMILY':
                return 4.2;
        }

        // active tickets & members
        switch(_update['table']){
            case 'TICKETOFFER': 
            case 'TICKETEXTRA': 
                return 5.1; 
            case 'TICKETOPTION': 
            case 'TICKETMIXEDPAY':
            case 'PINPADPAY':
                return 5.2; 

            case 'TICKETBAI': 
            case 'VERIFACTU':
            case 'TICKETSII':
            case 'TICKETDATA':
            case 'TICKETPRODUCT': 
            case 'TICKETDISCOUNT': 
                return 5.3;

            case 'TICKETCHANGE': 
            case 'TICKETINVOICE': 
                return 5.4;

            case 'SESSION': 
                return 5.5; 
                
            case 'TICKET': 
                return 5.6;
        }

        // waiter requests
        switch(_update['table']){
            case 'ASKWAITER':
                if (_update['status'] == 'AC'){
                    return 5.9;
                }
                break;
        }
        
        // place information (this is a place update)
        switch(_update['table']){
            case 'QRCODE': 
            case 'QRSCAN':
            case 'PRINTER':
            case 'SCALE':
            case 'CODESCAN':
            case 'PINPAD':
                return 6.1;

            case 'PAYMENT':
            case 'SWINVOICE':
                return 6.2;

            case 'PLACE': 
                return 6.3;
        }

        // items kept behind due to status
        switch(_update['table']){
            case 'QRCODE': return 8.1;
            case 'QRSCAN': return 8.1;
            case 'TICKETOFFER': return 8.1;
            case 'TICKETEXTRA': return 8.1;
            case 'TICKETDISCOUNT': return 8.1;
            case 'TICKETOPTION': return 8.2;
            case 'TICKETMIXEDPAY': return 8.2;
            case 'PINPADPAY': return 8.2;
            case 'TICKETPRODUCT': return 8.3;
            case 'TICKETBAI': return 8.3;
            case 'VERIFACTU': return 8.3;
            case 'TICKETSII': return 8.3;
            case 'TICKETDATA': return 8.3;
            case 'ASKWAITER': return 8.4;
            case 'TICKETINVOICE': return 8.4;
            case 'TICKETCHANGE': return 8.4;
            case 'SESSION': return 8.5;
            case 'TICKET': return 8.6;
        }

        // other instance information
        console.warn("WARNING: table '" + _update['table'] + "' not prioritized!");
        return 9;
    }
}

/**
 * Manages a mapping of UUIDs to object IDs for tracking resolved synchronization items.
 * 
 * This class provides a simple interface to store, retrieve, and delete mappings between
 * unique identifiers (UUIDs) and their corresponding object IDs, ensuring consistency
 * and avoiding reassignments.
 */
class ResolvedMap {
    private _uuids_done = new Map <string, string> ();

    /**
     * Adds a new mapping between a UUID and an object ID.
     * If the UUID is already mapped, an error is logged, and the mapping is not updated.
     * 
     * @param _uuid - The unique identifier (UUID) to associate.
     * @param objid - The object ID to associate with the UUID.
     */    
    Set(_uuid: string, objid: string): void{
        const _objid = this._uuids_done.get(_uuid);
        if (_objid && _objid != objid){
            console.error("[SYNC] Trying to reasign an existing uuid: '" + _uuid + "'");
        }

        this._uuids_done.set(_uuid, objid);
    }

    /**
     * Retrieves the object ID associated with a given UUID.
     * 
     * @param _uuid - The UUID to look up.
     * @returns The associated object ID, or `null` if the UUID is not found.
     */    
    Get(_uuid: string): string | null {
        return this._uuids_done.get(_uuid) || null;
    }

    /**
     * Removes a mapping for a given UUID if exists.
     * 
     * @param _uuid - The UUID whose mapping should be removed.
     */    
    Del(_uuid: string){
        if (this._uuids_done.has(_uuid)){
            this._uuids_done.delete(_uuid);
        }

    }
}

interface SessionMeta {
    deviceid: string, 
    sessionid: string,
    timestamp: number
}

interface PlaceMeta {
    placeid: string, 
    timestamp: number
}

/**
 * Manages cached session and place data for synchronization.
 * 
 * The `CacheManager` class facilitates the loading, saving, and maintaining of
 * cached data for sessions and places. It ensures that synchronization operations
 * can leverage locally stored data to reduce server load and improve performance.
 * 
 * ### Responsibilities:
 * - Manage `StorageDDBB` instances for session and place data.
 * - Handle the lifecycle of cached data, including loading and saving.
 * - Validate cached data to ensure freshness and relevance.
 * - Integrate with the `PendingQueue` to enqueue cached changes for processing.
 */
class CacheManager {
    private _databases = new Set <StorageDDBB> ();

    private _sessn_database = "upp-stored-session";
    private _place_database = "upp-stored-placeinfo-";

    /**
     * Creates a new instance of `CacheManager`.
     * 
     * @param sync - The synchronization service instance that this manager interacts with.
     */    
    constructor(public sync: syncService){
        // nothing to do
    }

    /**
     * Cleans up all cached databases and releases associated resources.
     * 
     * This method ensures that all open databases are closed and removed
     * from the internal set of managed databases.
     */    
    OnDestroy(){
        for (const _database of this._databases){
            _database.Close();
        }

        this._databases.clear();
    }

    /**
     * Provides access to the cache service.
     * 
     * @returns The `cacheService` instance associated with the synchronization service.
     */    
    get cache(): cacheService {
        return this.sync.cache;
    }

    /**
     * Provides access to the pending queue for managing synchronization updates.
     * 
     * @returns The `PendingQueue` instance from the `Syncronizator`.
     */    
    get pending(): PendingQueue {
        return this.sync.synchonizer.pending;
    }

    /**
     * Internal method to load session data from the specified database.
     * Validates the session metadata and enqueues the data for synchronization.
     * @param database - The storage backend to use.
     * @param session - The session ID to load.
     * @returns A promise resolving to `true` if the session data is valid, `false` otherwise.
     */
    private async _LoadSession(database: StorageDDBB, session: string): Promise <boolean> {
        if (!this.sync.device){
            throw new Error("[CACHE] Session is not ready for recovery!")
        } 

        const _metaitem = new StorageMeta(database);
        const _metadata: SessionMeta | null = await _metaitem.Get();
        if (!_metadata){
            return false;    // no stored data
        }

        if ((_metadata.deviceid != this.sync.device) || (_metadata.sessionid != session)){
            return false;   // not valid for this session
        } 

        const _elapsed = (new Date().getTime()) - _metadata.timestamp;
        if (_elapsed > AppConstants.CacheExpiration){
            console.warn("[STORAGE] Stored data are outdated!");
            return false;
        }

        // session data is valid: enqueue the items
        const _sessiondata: Array <any> = await (new StorageData(database)).GetAll();
        if (_sessiondata){
            console.info("[CACHE] Recovered " + _sessiondata.length + " changes from session cache");
            await this.pending.Concat(_sessiondata, false);
        }

        return true;    // session data is valid and is loading
    }

    /**
     * Loads session data from the cache and validates its freshness.
     * 
     * If the cached data is valid, it is enqueued into the `PendingQueue` for processing.
     * 
     * @param session - The session ID to load data for.
     * @returns A promise resolving to `true` if the session data is successfully loaded and valid, `false` otherwise.
     */       
    async LoadSession(session: string): Promise <boolean> {
        const _database: StorageDDBB = await this.cache.Open(this._sessn_database)
        if (_database){
            const _valid = await this._LoadSession(_database, session);
            if (!_valid){
                _database.Clean();
                _database.Close();
            }
            else {
                this._databases.add(_database);
            }

            return _valid;
        }

        return false;   // could not open database
    }

    /**
     * Internal method to save session data to the specified database.
     * @param database - The storage backend to use.
     * @param timestamp - The timestamp for the saved data.
     * @param changes - The list of changes to save.
     * @returns A promise resolving to `true` if the operation is successful, `false` otherwise.
     */
    private async _SaveSession(database: StorageDDBB, timestamp: number, changes: Array <any>): Promise <boolean> {
        if (!this.sync.device || !this.sync.session){
            throw new Error("[CACHE] Session is not ready for storage!")
        } 
        else {
            console.info("[CACHE] Storing " + changes.length + " changes into session cache" )
        }

        const _metaitem = new StorageMeta(database);
        const _metadata: SessionMeta = {
            deviceid: this.sync.device,
            sessionid: this.sync.session,
            timestamp: timestamp || new Date().getTime()
        }

        if (await _metaitem.Set(_metadata)){
            const _dataitem = new StorageData(database, (data) => { return data['table'] + '@' + data['objid'] });
            
            database.Begin('DATA');
            for (const _change of changes){
                await _dataitem.Set(_change);   // no lock as we are in transaction
            }
            return (await database.Flush('DATA'));
        }

        return false;
    }

    /**
     * Saves session data to the cache.
     * 
     * This method ensures that session metadata and changes are stored persistently,
     * and the storage is committed in a transactional manner.
     * 
     * @param changes - The list of changes to save.
     * @returns A promise resolving to `true` if the operation is successful, `false` otherwise.
     */    
    async SaveSession(changes: Array <any>): Promise <boolean> {
        const _database: StorageDDBB = await this.cache.Open(this._sessn_database)
        if (_database){
            this._databases.add(_database);
            return await this._SaveSession(_database, new Date().getTime(), changes);
        }

        return false;   // could not open database
    }

    /**
     * Internal method to load place data from the specified database.
     * Validates the place metadata and enqueues the data for synchronization.
     * @param database - The storage backend to use.
     * @param placeid - The place ID to load.
     * @returns A promise resolving to `true` if the place data is valid, `false` otherwise.
     */
    private async _LoadPlace(database: StorageDDBB, placeid: string): Promise <boolean> {
        const _metaitem = new StorageMeta(database);
        const _metadata: PlaceMeta | null = await _metaitem.Get();
        if (!_metadata){
            return false;    // no stored data
        }        

        if (_metadata.placeid != placeid){
            return false;   // not valid for this place
        } 

        const _elapsed = (new Date().getTime()) - _metadata.timestamp;
        if (_elapsed > AppConstants.CacheExpiration){
            console.warn("[STORAGE] Stored data are outdated!");
            return false;
        }

        // place data is valid: enqueue the items
        const _placedata: Array <any> = await (new StorageData(database)).GetAll();
        if (_placedata){
            console.info("[CACHE] Recovered " + _placedata.length + " changes from place [" + placeid + "] cache");
            await this.pending.Concat(_placedata, false);
        }

        return true;    // place data is valid and is loading
    }

    /**
     * Loads place data from the cache and validates its freshness.
     * 
     * If the cached data is valid, it is enqueued into the `PendingQueue` for processing.
     * 
     * @param placeid - The place ID to load data for.
     * @returns A promise resolving to `true` if the place data is successfully loaded and valid, `false` otherwise.
     */    
    async LoadPlace(placeid: string): Promise <boolean> {
        const _database: StorageDDBB = await this.cache.Open(this._place_database + placeid);
        if (_database){
            const _valid = await this._LoadPlace(_database, placeid);
            if (!_valid){
                _database.Clean();
                _database.Close();
            }
            else {
                this._databases.add(_database);
            }

            return _valid;            
        }

        return false;   // could not open database
    }

    /**
     * Internal method to save place data to the specified database.
     * @param database - The storage backend to use.
     * @param timestamp - The timestamp for the saved data.
     * @param changes - The list of changes to save.
     * @returns A promise resolving to `true` if the operation is successful, `false` otherwise.
     */
    private async _SavePlace(database: StorageDDBB, timestamp: number, changes: Array <any>): Promise <boolean> {
        if (!this.sync.place || !this.sync.place.objid){
            throw new Error("[CACHE] Session is not ready for storage!")
        } 
        else {
            console.info("[CACHE] Storing " + changes.length + " changes into place [" + this.sync.place.objid + "] cache" )
        }

        const _metaitem = new StorageMeta(database);
        const _metadata: PlaceMeta = {
            placeid: this.sync.place.objid,
            timestamp: timestamp || new Date().getTime()
        }

        if (await _metaitem.Set(_metadata)){
            const _dataitem = new StorageData(database, (data) => { return data['table'] + '@' + data['objid'] });
            
            database.Begin('DATA');
            for (const _change of changes){
                await _dataitem.Set(_change);   // no lock as we are in transaction
            }
            return (await database.Flush('DATA'));
        }

        return false;
    }

    /**
     * Saves place data to the cache.
     * 
     * This method ensures that place metadata and changes are stored persistently,
     * and the storage is committed in a transactional manner.
     * 
     * @param placeid - The ID of the place to save data for.
     * @param changes - The list of changes to save.
     * @returns A promise resolving to `true` if the operation is successful, `false` otherwise.
     */    
    async SavePlace(placeid: string, changes: Array <any>): Promise <boolean>{
        const _database: StorageDDBB = await this.cache.Open(this._place_database + placeid);
        if (_database){
            this._databases.add(_database);
            return await this._SavePlace(_database, new Date().getTime(), changes);
        }

        return false;   // could not open database
    }
}

/**
 * Page visibility + refresh watchdog that detects zombie `/sync` long-polls after OS suspend.
 *
 * Owns listener lifecycle, hidden/dedup/recovering state, and the watchdog timer.
 * Queries {@link Syncronizator} for state and calls {@link Syncronizator.StaleRefresh} to recover.
 */
class StaleRefreshGuard {
    /** Minimum time hidden before a visibility wake recovers (ms). Shorter when a refresh is in flight. */
    private readonly _WAKE_MIN_HIDDEN_MS = 3000;

    /** Dedup window so visibility + pageshow (bfcache) do not double-recover. */
    private readonly _WAKE_DEDUP_MS = 3000;

    /** Cancel a refresh that never returns (OS suspend freezes timers + XHR). Above server long-poll (~20s). */
    private readonly _REFRESH_WATCHDOG_MS = 45000;

    private _pageHiddenAt: number | null = null;
    private _wakeDedupUntil = 0;
    private _recovering = false;
    private _refreshWatchdog: ReturnType<typeof setTimeout> | null = null;

    private _visibilityHandler = () => {
        if (typeof document === 'undefined'){
            return;
        }
        if (document.hidden){
            this._pageHiddenAt = Date.now();
            console.info("[SYNC] Page hidden");
        }
        else {
            void this._OnPageBecameVisible('visibility');
        }
    };

    private _pageshowHandler = (event: PageTransitionEvent) => {
        if (event.persisted){
            void this._OnPageBecameVisible('pageshow');
        }
    };

    /**
     * @param syncronizator - Owning synchronizer (state + recover action).
     */
    constructor(private syncronizator: Syncronizator){
        if (typeof document !== 'undefined'){
            document.addEventListener('visibilitychange', this._visibilityHandler);
        }
        if (typeof window !== 'undefined'){
            window.addEventListener('pageshow', this._pageshowHandler);
        }
    }

    /** Removes page listeners and clears the watchdog timer. */
    OnDestroy(): void {
        if (typeof document !== 'undefined'){
            document.removeEventListener('visibilitychange', this._visibilityHandler);
        }
        if (typeof window !== 'undefined'){
            window.removeEventListener('pageshow', this._pageshowHandler);
        }
        this.DisarmWatchdog();
    }

    /** Starts (or restarts) the in-flight refresh watchdog. */
    ArmWatchdog(): void {
        this.DisarmWatchdog();
        this._refreshWatchdog = setTimeout(() => {
            this._refreshWatchdog = null;
            void this._Recover('watchdog');
        }, this._REFRESH_WATCHDOG_MS);
    }

    /** Clears the in-flight refresh watchdog. */
    DisarmWatchdog(): void {
        if (this._refreshWatchdog){
            clearTimeout(this._refreshWatchdog);
            this._refreshWatchdog = null;
        }
    }

    /**
     * Page became visible again (unlock / bfcache). Recover a zombie long-poll when
     * a refresh is in flight or the tab was hidden long enough.
     */
    private async _OnPageBecameVisible(source: 'visibility' | 'pageshow'): Promise<void> {
        if (this.syncronizator.IsUnloading){
            return;
        }

        const _hiddenAt = this._pageHiddenAt;
        this._pageHiddenAt = null;

        const _elapsed = (_hiddenAt != null) ? (Date.now() - _hiddenAt) : this._WAKE_MIN_HIDDEN_MS;
        const _needRecover = this.syncronizator.IsRefreshing || (_elapsed >= this._WAKE_MIN_HIDDEN_MS);
        if (!_needRecover){
            return;
        }

        console.info("[SYNC] Page visible again (" + source + ", hidden " + Math.floor(_elapsed / 1000) + "s)");
        await this._Recover('wake');
    }

    /**
     * Dedup + gate, then ask the synchronizer to abort a stuck `/sync` and force a fresh cycle.
     */
    private async _Recover(reason: 'wake' | 'watchdog'): Promise<void> {
        if (this.syncronizator.IsUnloading || this._recovering){
            return;
        }

        const _now = Date.now();
        if (_now < this._wakeDedupUntil){
            return;
        }
        this._wakeDedupUntil = _now + this._WAKE_DEDUP_MS;

        if (this.syncronizator.connection.status != 'RUNNING'){
            return;
        }

        this._recovering = true;
        try {
            console.warn("[SYNC] Recovering stale refresh (" + reason + ")");
            await this.syncronizator.StaleRefresh();
        }
        catch (error) {
            console.error("[SYNC] Stale refresh recovery failed (" + reason + ")", error);
        }
        finally {
            this._recovering = false;
        }
    }
}

/**
 * Handles the synchronization of data between a client and a server.
 * 
 * The `Syncronizator` class is responsible for managing changes, resolving dependencies,
 * refreshing data, and persisting changes during synchronization sessions. It interacts
 * with various services such as `Connection`, `PendingQueue`, `ResolvedMap`, and
 * `StaleRefreshGuard` (visibility/watchdog recovery of stuck `/sync` long-polls).
 */
class Syncronizator {
    private _doRefreshUrl = 'sync';

    /**
     * /sync with pending writes returns without the empty long-poll; bound the wait so
     * UI-forced flush cannot hang on a half-open reconnect. Empty polls keep default HTTP timeout.
     */
    private static readonly _SYNC_WRITE_TIMEOUT_MS = 15000;

    private _onrefresh = false;                                   // set to true if refresh is ongoing
    private _unloading = false;                                   // pagehide/unload: stop sync, no reconnect
    private _timestamp = new Map <LoadTarget, number> ();         // timestamp of last succesfull refresh
    private _reference = 0;                                       // time reference from the server

    private _syncchanges: CommitChange[] = [];
    private _waitchanges: CommitChange[] = [];
    private _sentchanges: CommitChange[] = [];

    private _onRefreshCompleted = new Subject<void>();
    /** Observable for refresh completion notifications. */    
    public OnRefreshCompleted = this._onRefreshCompleted.asObservable();

    private _onCommitCompleted = new Subject <void> ();
    /** Observable for commit completion notifications. */    
    public OnCommitCompleted = this._onCommitCompleted.asObservable();

    private _onchange_subscription: Subscription | null = null;
    private _wsstore_subscription: Subscription | null = null;
    private _onlineWake: Subscription | null = null;
    /** Resolves the in-flight transport/offline delay when connectivity returns. */
    private _releaseDelay: (() => void) | null = null;

    private _onChange = new Subject<any>();     // change recovered from server
    /** Observable for changes received from the server. */    
    public OnChange = this._onChange.asObservable();

    private _place: Place | null = null;

    /** Abort handle for the in-flight `/sync` DoRequest (client XHR). */
    private _refreshCancel: AdhocRequestCancel | null = null;
    /** True while CancelRefresh aborted the client XHR — skip HTTPER backoff for that cycle. */
    private _clientAbortRefresh = false;

    private _staleGuard: StaleRefreshGuard | null = null;

    /**
     * Creates a new instance of the `Syncronizator`.
     * @param sync - The synchronization service used by the `Syncronizator`.
     */    
    constructor(public sync: syncService){
        this._wsstore_subscription = this.connect.store.Ready$.subscribe((ready) => {
            void this.OnStoreReady(ready);
        });
    }

    /**
     * Gets the stale-refresh guard (visibility wake + watchdog) for zombie `/sync` recovery.
     */
    get staleGuard(): StaleRefreshGuard {
        if (!this._staleGuard){
            this._staleGuard = new StaleRefreshGuard(this);
        }
        return this._staleGuard;
    }

    /**
     * True while a `/sync` refresh cycle is in flight.
     */
    get IsRefreshing(): boolean {
        return this._onrefresh;
    }

    /**
     * True after `pagehide`/`unload`: no further sync cycles or CONNECTED recreate.
     */
    get IsUnloading(): boolean {
        return this._unloading;
    }

    /**
     * Abort a stuck `/sync`, promote pending writes, and force a fresh refresh cycle.
     * Invoked by {@link StaleRefreshGuard} after wake/watchdog gating.
     */
    async StaleRefresh(): Promise<void> {
        if (this._onrefresh){
            await this.CancelRefresh();
        }

        this._PromoteWaitChanges();
        this._ResetFailureState();

        if (!this._unloading && (this.connection.status == 'RUNNING')){
            await this.DoRefresh(false, true);
        }
    }

    /**
     * Stops refresh/reconnect before `connection/unload` beacon so a closing tab
     * cannot race DE→AC via check_session. A full window refresh starts a new
     * Syncronizator and may revive the DE session within RefreshTime.
     */
    OnUnload(): void {
        if (this._unloading){
            return;
        }

        this._unloading = true;
        console.warn("[SYNC] Page unload: halting sync cycles (no reconnect)");
        this.connection.HaltForPageUnload();
    }

    /**
     * Cleans up resources when the `Syncronizator` is destroyed.
     */    
    OnDestroy(){
        this.OnUnload();

        if (this._staleGuard){
            this._staleGuard.OnDestroy();
            this._staleGuard = null;
        }

        if (this._refreshCancel?.abort){
            this._refreshCancel.abort();
        }
        this._refreshCancel = null;

        if (this._pending){
            this._pending.OnDestroy();
            this._pending = null;
        }

        if (this._cache){
            this._cache.OnDestroy();
            this._cache = null;
        }

        if (this._wsstore_subscription){
            this._wsstore_subscription.unsubscribe();
            this._wsstore_subscription = null;
        }

        if (this._onchange_subscription){
            this._onchange_subscription.unsubscribe();
            this._onchange_subscription = null;
        }

        if (this._onlineWake){
            this._onlineWake.unsubscribe();
            this._onlineWake = null;
        }

        if (this._releaseDelay){
            this._releaseDelay();
        }
    }

    private _connection: Connection | null = null;

    /**
     * Gets the connection instance for managing server interactions.
     */    
    get connection(): Connection {
        if (!this._connection){
            this._connection = new Connection(this.sync);
        }
        return this._connection;
    }

    private _pending: PendingQueue | null = null;

    /**
     * Gets the pending queue instance for managing pending changes.
     */    
    get pending(): PendingQueue {
        if (!this._pending){
            this._pending = new PendingQueue(this.sync);
        }
        return this._pending;
    }

    private _resolved: ResolvedMap | null = null;

    /**
     * Gets the resolved map instance for managing resolved UUID mappings.
     */    
    get resolved(): ResolvedMap {
        if (!this._resolved){
            this._resolved = new ResolvedMap();
        }
        return this._resolved;
    }

    private _cache: CacheManager | null = null;

    /**
     * Gets the cache manager for previous data recovering.
     */        
    get cache(): CacheManager {
        if (!this._cache){
            this._cache = new CacheManager(this.sync);
        }
        return this._cache;
    }

    /**
     * Gets the current connection status.
     */    
    get status(): ConnectionStatus {
        return this.connection.status;        
    }

    /**
     * Sets the current connection status.
     */    
    set status(value: ConnectionStatus){
        this.connection.status = value;
    }

    /**
     * Provides access to the state service.
     */    
    get state(): stateService {
        return this.sync.state;
    }

    /**
     * Provides access to the ad-hoc service.
     */    
    get adhoc(): adhocService {
        return this.sync.adhoc;
    }

    /**
     * Single-instance facade over the local `pispas-connect` transports (dual-write
     * store + activity logger); see {@link wsconnectService.store} / {@link wsconnectService.logger}.
     */    
    get connect(): wsconnectService {
        return this.sync.connect;
    }

    /**
     * Provides access to the language service.
     */    
    get lang(): languageService {
        return this.sync.lang;
    }

    /**
     * Provides access to the toast notification service.
     */    
    get toast(): toastService {
        return this.sync.toast;
    }

    /**
     * Provides access to the last server timestamp update
     */ 
    timestamp(target:  LoadTarget): number {
        return this._timestamp.get(target) || 0;
    }

    /**
     * Starts the synchronization process by loading pending changes and establishing a connection.
     * @param session - The session ID for the connection.
     * @returns A promise that resolves to `true` if the operation is successful, `false` otherwise.
     */    
    async Start(session: string | null) : Promise <boolean> {
        this.state.session = session;

        console.info("[SYNC] Searching for pending changes..");
        const _changes = await this.GetStoredChanges();
        if (_changes && _changes.length > 0){
            console.warn("[SYNC] Found " + _changes.length + " changes from previous sessions");
            // Stage in wait so the first DoRefresh can reconcile against server updates
            // in _resolve_response before promoting to the send queue.
            this._waitchanges = _changes;
        }
        else {
            console.info("[SYNC] No changes from previous sessions (up to date)");
        }

        this._onchange_subscription = this.pending.OnChange.subscribe(
        (change) => {
            this._onChange.next(change);

            if (('_uuid' in change) && (change['_uuid'])){
                this.resolved.Del(change['_uuid']);
            }
        });
        
        // recover session data from the cache before querying the server
        let _cacheOk = false;
        if (session){
            try {
                _cacheOk = await this.cache.LoadSession(session);
            }
            catch(error: any){
                console.error("[SYNC] Error recovering session information: Continuing without data..", error)
            }    
        }

        // create the server connection to be notified on updates
        if (session){
            return await this.connection.CreateConnection({ allowDegraded: _cacheOk });
        }
        else {
            return await this.connection.ReleaseConnection();
        }          
    }

    /**
     * Stops the synchronization process and releases resources.
     */    
    async Stop(){
        if (this.connection.status != 'STOPPED'){
            await this.connection.ReleaseConnection();

            if (this._onchange_subscription){
                this._onchange_subscription.unsubscribe();
                this._onchange_subscription = null;
            }

            this.state.place = null;
        }
    }

    /***************************************/
    /* ENTRY POINT                         */
    /***************************************/

    private _placeid: string | null = null;
    private _tableid: string | null = null;

    /**
     * Updates the connection to associate a user.
     * @param user - The user to associate, or `null` to remove the user.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */    
    async SetUser(user: User | null): Promise <boolean> {
        const _success = await this.connection.SetUser(user);
        if (_success){
            this._timestamp.set('SESSION', 0);
            this.pending.Reset();
        }
        return _success;          
    }

    /**
     * Binds guest `/sync ?table=` to QRSCAN.objid and place from the scan.
     * @param qrscan - The scan to associate, or `null` to clear it.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */
    async SetQrScan(qrscan: QrScan | null): Promise <boolean> {
        const _success = await this.connection.SetQrScan(qrscan);
        if (_success){
            this._tableid = qrscan?.objid ?? null;
            if (qrscan?.place?.objid){
                this.state.place = qrscan.place.objid;
            }
            this.pending.Reset();
        }
        return _success;
    }

    /**
     * Updates the connection to associate a QR code.
     * @param qrcode - The QR code to associate, or `null` to remove the QR code.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */
    async SetQrCode(qrcode: QrCode | null): Promise <boolean> {
        const _scan = qrcode?.qrscan ?? this.sync.qrscan ?? null;
        const _scanid = qrcode ? (_scan?.objid ?? null) : null;
        const _placeid = qrcode?.place?.objid ?? _scan?.place?.objid ?? null;

        const _success = await this.connection.SetQrCode(qrcode);
        if (_success){
            this._timestamp.set('QRCODE', 0);
            this._tableid = _scanid;
            
            if (_placeid){
                this.state.place = _placeid;
            }
            
            this.pending.Reset();
        }
        return _success;        
    }

    /**
     * Updates the connection to associate a place.
     * @param place - The place to associate, or `null` to remove the place.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */    
    async SetPlace(place: Place | null): Promise <boolean> {
        // Bind place before opening the backup transport so Ready$ handlers see
        // the correct target (Ready$ may emit during await store.SetPlace).
        if (this._place && this._place !== place){
            this._place.BackupReady = false;
        }

        this._place = place;

        await this.connect.SetPlace(place);

        // Already-true Ready$ does not re-emit; re-enter recovery when transport is up.
        if (place?.RequiresLocalBackup && this.connect.store.IsReady){
            void this.OnStoreReady(true);
        }

        if (place){
            Ticketbai.PreloadLibraries(place, this.sync.http.IsOnline);
        }

        const _placeid = place?.objid ?? null;
        
        // recover place data from the cache before querying the server
        if (place && place.objid){
            try {
                await this.cache.LoadPlace(place.objid);
            }
            catch(error: any){
                console.error("[SYNC] Error recovering session information: Continuing without data..", error)
            }
        }

        const _success = await this.connection.SetPlace(place);
        if (_success){
            this._timestamp.set('PLACE', 0);
            this._placeid = _placeid;
            this.state.place = _placeid;
            this.pending.Reset();
        }
        return _success;
    }

    /*********************************/
    /* PENDING CHANGES PERSISTANCE   */
    /*********************************/

    private _storedsynckey = 'upp-stored-syncdata';
    private _storedsyncavl = false;     // avoid clearing if not set

    /**
     * Loads pending commit packages from the dual-write store (browser primary).
     * @returns Stored changes, or undefined/null when none.
     */    
    private async GetStoredChanges(): Promise <CommitChange[]> {
        const _changes = await this.connect.store.Get(this._storedsynckey) as CommitChange[];
        if (_changes){
            this._storedsyncavl = true;     // changes available in storage
        }

        return _changes;
    }

    /**
     * Saves changes through the dual-write store (browser + optional local backup).
     * @param changes - The changes to store.
     */    
    private async SetStoredChanges(changes: CommitChange[]): Promise <void> {
        if (changes.length > 0){
            await this.connect.store.Set(this._storedsynckey, changes);
            this._storedsyncavl = true;     // changes available in storage
        }        
    }

    /**
     * Removes stored changes through the dual-write store.
     */    
    private async DelStoredChanges(): Promise <void> {
        if (this._storedsyncavl){
            this._storedsyncavl = false;    // removed changes form storage
            await this.connect.store.Del(this._storedsynckey);
        }
    }

    /**
     * Reacts to local backup transport readiness: recovers packages then confirms
     * `BackupReady` only after a successful recover pass, or clears it immediately
     * on disconnect. A recover failure leaves `BackupReady` false until the next
     * ready transition (reconnect / SetPlace).
     * @param ready Whether the backup transport is connected.
     */
    private async OnStoreReady(ready: boolean): Promise <void> {
        const _place = this._place;
        if (!_place?.RequiresLocalBackup){
            return;
        }

        if (!ready){
            _place.BackupReady = false;
            return;
        }

        try {
            const _backup = await this.connect.store.GetBackup(this._storedsynckey);
            if (Array.isArray(_backup) && _backup.length > 0){
                // identity key for a commit package (table + objid/_uuid), same as FlushChanges
                const _changeKey = (change: any) => {
                    return change['relation']['table'] + '@' + (change['relation']['objid'] || change['relation']['_uuid']);
                };

                const _known = new Map<string, CommitChange>();
                for (const _change of this._syncchanges){
                    _known.set(_changeKey(_change), _change);
                }
                for (const _change of this._waitchanges){
                    _known.set(_changeKey(_change), _change);
                }
                for (const _change of this._sentchanges){
                    _known.set(_changeKey(_change), _change);
                }

                const _added: CommitChange[] = [];
                for (const _change of _backup as CommitChange[]){
                    if (!_change?.relation){
                        continue;
                    }

                    const _key = _changeKey(_change);
                    if (_known.has(_key)){
                        continue;
                    }

                    _known.set(_key, _change);
                    _added.push(_change);
                }

                if (_added.length > 0){
                    console.warn('[SYNC] Recovered ' + _added.length + ' changes from local backup');
                    this._waitchanges.push(..._added);
                    void this.SetStoredChanges([
                        ...this._syncchanges,
                        ...this._waitchanges,
                        ...this._sentchanges,
                    ]);
                }
                else {
                    console.info('[SYNC] Local backup had no new pending changes');
                }
            }

            _place.BackupReady = true;
        }
        catch (error: unknown) {
            console.error('[SYNC] Error recovering changes from local backup', error);
            _place.BackupReady = false;
        }
    }

    /*********************************/
    /* SERVER REFERENCED TIMING      */
    /*********************************/

    private _serverreference = 0;    // server elapsed time reference

    private get serverreference(): number{
        return Math.round((performance.now() - this._serverreference) / 1000);
    }
    
    private set serverreference(value: number){
        this._serverreference = value;
    }

    /*********************************/
    /* MAIN REFRESH METHODS (COMMIT) */
    /*********************************/

    /**
     * Determines if one change depends on another.
     * 
     * This method checks whether a given change (`a`) is directly or indirectly dependent
     * on another change (`b`) based on their relations.
     * 
     * @param a - The change to check for dependencies.
     * @param b - The potential dependency.
     * @returns `true` if `a` depends on `b`, otherwise `false`.
     */    
    private CheckDepends(a: CommitChange, b: CommitChange){
        const _relation = b.relation;

        for(const _dfield in a.requires){
            // check if a is direct dependecy of b
            const _requires = a.requires[_dfield].relation;
            if ('_uuid' in _requires){  
                if ((_relation['table'] == _requires['table']) && (_relation['_uuid'] == _requires['_uuid'])){
                    return true;    // a is direct dependency of b
                }
            }

            // check if a is indirect dependency of b
            const _indirect = a.requires[_dfield].requires;
            for (const _ifield in _indirect){
                if (this.CheckDepends(_indirect[_ifield], b)){
                    return true;    // a is indirect dependency of b
                }    
            }
        }

        return false;    // a is not a dependency of b
    }

    /**
     * Finds the index of a required relation in a map of changes.
     * 
     * @param _require - The relation to search for.
     * @param _changes - A map of change keys to their indices.
     * @returns The index of the related change, or `-1` if not found.
     */    
    private RelatedIndex(_require: any, _changes: Map <string, number>){
        const _chngekey = _require['table'] + '@' + _require['_uuid'];

        if (!_require || !_require['table'] || !_require['_uuid']) {
            console.error("[COMMIT ERROR]: Invalid _require object:", _require);
            return -1;  // invalid require data
        }
        
        const _position = _changes.get(_chngekey);
        if (typeof _position !== 'undefined'){
            return _position;
        }

        return -1;  // relation not found
    }

    /**
     * Sorts changes so dependencies come first (same pivot-swap as CheckChanges).
     */
    private _sort_syncchanges(changes: CommitChange[]): void {
        let _pvt = 0;
        while(_pvt < changes.length){
            let _hasDepends = false;
            for(let _idx = _pvt+1; _idx < changes.length; _idx++){
                _hasDepends = this.CheckDepends(changes[_pvt], changes[_idx]);
                if (_hasDepends){
                    const _swap = changes[_idx];
                    changes[_idx] = changes[_pvt];
                    changes[_pvt] = _swap;

                    break;  // dependecy found (swapped with current)
                }
            }

            if (!_hasDepends){
                _pvt++;     // pivot has no dependencies, move to next
            }
        }
    }

    /**
     * Whether a queue entry matches a require keyed by table@_uuid.
     */
    private _has_required(queue: CommitChange[], require: any): boolean {
        if (!require || !require['table'] || !require['_uuid']){
            return false;
        }

        const _key = require['table'] + '@' + require['_uuid'];
        for (const _change of queue){
            const _relation = _change.relation;
            if (_relation && ((_relation['table'] + '@' + _relation['_uuid']) == _key)){
                return true;
            }
        }

        return false;
    }

    /**
     * Index of an open require's parent for CheckChanges, or -1 if missing.
     * Same contract as RelatedIndex, plus queues / ResolvedMap / live InCommit:
     * when the parent is not in this batch but already accepted elsewhere,
     * returns 0 (never fails the bad-order check). ResolvedMap rewrites
     * require.objid.
     */
    private _find_required(require: any, batchMap: Map<string, number>): number {
        if (!require || !require['table'] || !require['_uuid']){
            return -1;
        }

        const _pos = this.RelatedIndex(require, batchMap);
        if (_pos >= 0){
            return _pos;
        }

        if (this._has_required(this._sentchanges, require)){
            return 0;
        }

        if (this._has_required(this._syncchanges, require)){
            return 0;
        }
        
        if (this._has_required(this._waitchanges, require)){
            return 0;
        }

        const _objid = this.resolved.Get(require['_uuid']);
        if (_objid){
            require['objid'] = _objid;
            return 0;
        }

        // Align with data.Commit: parent already pushed and awaiting server objid.
        if (this.sync.IsInCommit?.(require['table'], require['_uuid'])){
            return 0;
        }

        return -1;  // not found in this batch
    }

    /**
     * Validates the integrity of changes by checking dependencies and order.
     * 
     * Ensures that changes are sorted in the correct order based on their dependencies
     * and checks for missing or improperly ordered relations. Open requires may also
     * be satisfied by in-flight / staging queues or ResolvedMap (not only the batch).
     * 
     * @param changes - The list of changes to validate.
     * @returns `true` if all changes are valid and correctly ordered, otherwise throws an error.
     * @throws If a missing or misordered relation is detected.
     */    
    private CheckChanges(changes: CommitChange[]): boolean {
        this._sort_syncchanges(changes);

        // build an index map for quick relation search
        const _relationMap = new Map <string, number> ()
        for (let _idx = 0; _idx < changes.length; _idx++){
            const relation = changes[_idx].relation
            const chngekey = relation['table'] + '@' + relation['_uuid'];

            _relationMap.set(chngekey, _idx);
        }

        // check for missing relations
        for(let _idx=0; _idx < changes.length; _idx++){
            const _change = changes[_idx];
            for(const _field in _change.requires){
                const _require = _change.requires[_field].relation;
                if (!('objid' in _require) || (!_require.objid)){
                    let _message: string | null = null

                    const _pos = this._find_required(_require, _relationMap);
                    if (_pos == -1){
                        _message = "@commit_error_relation_not_found";
                    }
                    else if (_pos > _idx){
                        _message = "@commit_error_relation_bad_order";
                    }

                    if (_message){
                        const _error = {
                            missing: _change,
                            require: _require,
                            changes: changes.slice(0),
                            message: _message
                        };

                        console.error("[COMMIT ERROR]: ", JSON.stringify(_error));
                        
                        throw(_error);    
                    }
                }
            }
        }

        return true;
    }

    private _logcommits = false;

    /**
     * Commits a set of changes to the server, ensuring dependency order and integrity.
     * 
     * While a refresh is in flight (`_onrefresh`), new commits are staged in `_waitchanges`
     * so they are not mixed into the in-flight `_sentchanges` package and can be reconciled
     * in `_resolve_response`. Otherwise they go to `_syncchanges` (the send queue).
     * After cancelling any ongoing refresh, wait packages are promoted and the full pending
     * queue is persisted (not only the last flush package).
     * 
     * @param changes - The list of changes to commit.
     * @param force - When `true`, wait for `/sync` ack and return that result; when `false`,
     *   stage into the send queue and return `true` after a successful local enqueue.
     * @returns A promise resolving to `true` if the operation succeeds, otherwise `false`.
     */    
    async FlushChanges(changes: CommitChange[], force = false) : Promise <boolean> {
        const _targetlist: CommitChange[] = (this._onrefresh) ? this._waitchanges : this._syncchanges;

        function _changeKey(change: CommitChange): string {
            return change.relation['table'] + '@' + (change.relation['objid'] || change.relation['_uuid']);
        }

        const _targetmap = new Map <string, CommitChange> ();

        console.info("[COMMIT] committing " + changes.length + " entries (now = " + force + ")");
        if (changes.length == 0){
            return true;    // no changes to commit
        }

        for (const _target of _targetlist){
            _targetmap.set(_changeKey(_target), _target);
        }

        // complete the target list with the incoming changes
        for(const change of changes){
            const _changekey: string = _changeKey(change);

            if (this._logcommits){
                console.info("[COMMIT] on table '" + change.entrynfo['table'] + "' (" + (this._onrefresh ? 'waitchanges' : 'syncchanges') + "):", change.entrynfo);
                const _previous: CommitChange | undefined = _targetmap.get(_changekey);
                if (_previous){
                    console.info("[COMMIT] remove previous '" + _previous.entrynfo['table'] + "':", change.entrynfo);
                }
            }            

            _targetmap.set(_changekey, change);  // replace any previous changes over this object
        }        

        // check the changes integrity and commit
        const _tocommit: CommitChange[] = [..._targetmap.values()];
        try{
            if (this.CheckChanges(_tocommit)){
                _targetlist.splice(0, _targetlist.length, ..._tocommit);
        
                const _cancel = performance.now();
                await this.CancelRefresh();
                console.info("[COMMIT] Cancel ongoing completed in: " + (performance.now() - _cancel).toFixed(2) + " ms");

                // CancelRefresh may complete DoRefresh's finally (wait→sync). If no refresh
                // was running, promote any staged wait left from Start/backup recovery.
                this._PromoteWaitChanges();

                // Persist sync + wait + sent, not only the flush package.
                void this.SetStoredChanges([
                    ...this._syncchanges,
                    ...this._waitchanges,
                    ...this._sentchanges,
                ]);

                if (force){
                    const _forced = performance.now();
                    const _ok = await this.ForceRefresh();
                    console.info("[COMMIT] Forced commit completed in: " + (performance.now() - _forced).toFixed(2) + " ms (ok = " + _ok + ")");
                    return _ok;
                }
            };   
            
            return true;
        }
        catch(error: any){
            this.toast.ShowAlert('danger', this.lang.tr(error.message));
        }

        return false;
    }

    /*********************************/
    /* MAIN REFRESH METHODS (UPDATE) */
    /*********************************/

    /**
     * Resolves changes in the synchronization list by adding server references and
     * converting "insert" actions to "update" when applicable.
     */    
    private _resolve_syncchanges(): void {
        const _serverreference = this._reference + this.serverreference;  // adjust local interval to server time

        for(const _change of this._syncchanges){
            _change.reference = _serverreference;

            if (_change.entrynfo['_actn'] == "do_insert"){
                const _objid = this.resolved.Get(_change.entrynfo['_uuid']);
                if (_objid){
                    _change.entrynfo['objid'] = _objid;
                    _change.entrynfo['_actn'] = "do_update";
                }
            }
            
            this._resolve_change(_change);  // fill require objids from ResolvedMap before the completeness check.
        }
    }

    private _lastchanges = 0;   // reduce number of logs (only on size changed)

    /**
     * True when the whole sync package is coherent: every open require has objid
     * or its parent travels in the same array. Atomic — no partial send.
     */
    private _check_complete(changes: CommitChange[]): boolean {
        const _uuidKeys = new Set <string> ();
        for (const _change of changes){
            const _relation = _change.relation;
            if (_relation && _relation['_uuid']){
                _uuidKeys.add(_relation['table'] + '@' + _relation['_uuid']);
            }
        }

        for (const _change of changes){
            for (const _field in _change.requires){
                const _require = _change.requires[_field].relation;
                if (('objid' in _require) && _require.objid){
                    continue;
                }

                if (!_require || !_require['table'] || !_require['_uuid']){
                    return false;
                }

                if (!_uuidKeys.has(_require['table'] + '@' + _require['_uuid'])){
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Moves staged `_waitchanges` into `_syncchanges` so packages are never
     * stranded on early exits or left out of an atomic send package.
     */
    private _PromoteWaitChanges(): void {
        if (this._waitchanges.length > 0){
            this._syncchanges.push(...this._waitchanges);
            this._waitchanges = [];
        }
    }

    /**
     * Prepares the current changes for synchronization by resolving dependencies
     * and adjusting server references.
     *
     * The sync queue is atomic: either the whole package is coherent and moves to
     * `_sentchanges`, or nothing is sent and `_syncchanges` is left untouched.
     *
     * @returns The number of changes prepared for synchronization.
     */    
    private _prepare_syncronize(): number {
        this._resolve_syncchanges();

        if ((this._syncchanges.length > 0) && (this._lastchanges != this._syncchanges.length)){
            console.info("[STORAGE]: Updating the changeset with " + this._syncchanges.length + " changes.");
        }
        this._lastchanges = this._syncchanges.length;

        if (!this._check_complete(this._syncchanges)){
            console.info(
                "[SYNC] Delaying sync package until dependencies resolve ("
                + this._syncchanges.length + " changes)"
            );
            this._sentchanges = [];
            return 0;
        }

        this._sort_syncchanges(this._syncchanges);
        
        this._sentchanges = this._syncchanges;
        this._syncchanges = [];

        return this._sentchanges.length;
    }

    private readonly HTTPER_DELAY_BASE_MS = 2000;
    private readonly HTTPER_DELAY_MAX_MS = 60000;
    private readonly SERVER_DELAY_BASE_MS = 10000;
    private readonly SERVER_DELAY_MAX_MS = 120000;
    private readonly SERVER_RETRIES = 5; // Application failures before discarding the queue (interim until quarantine)

    private _httperfailures = 0;
    private _serverretries = this.SERVER_RETRIES;

    /**
     * Handles the successful completion of a synchronization operation.
     *
     * Clears stored changes only when the server confirmed they were applied (`updated`).
     */
    private _onsuccess_syncronize(updated: boolean){
        if (updated){   // clear the changes stored in cache
            this.DelStoredChanges();
            this._sentchanges = [];
            this._ResetFailureState();
        }
        else {          // try to resend on the next loop
            this._syncchanges.push(...this._sentchanges);
            this._sentchanges = [];
        }
    }

    /**
     * Restores or discards sent changes after a failed sync attempt.
     *
     * Transport failures never discard the pending queue (keep + backoff).
     * Application/server failures retry a fixed number of times, then discard
     * and alert (interim until quarantine archives the payload first).
     */
    private _onfailure_synchronize(source: 'HTTPER' | 'SERVER'){
        const _hadpending = (this._syncchanges.length > 0) || (this._sentchanges.length > 0);

        if (source == 'HTTPER'){
            this._syncchanges.push(...this._sentchanges);
            this._sentchanges = [];
            if (_hadpending){
                this._httperfailures++;
                console.warn("[SYNC] Cannot commit changes (transport failure #" + this._httperfailures + ") — queue kept");
            }
            return;
        }

        if (_hadpending){
            console.warn("[SYNC] Cannot commit changes (pending server retries " + this._serverretries + ")!");

            if (--this._serverretries == 0){
                this._serverretries = this.SERVER_RETRIES;
                
                this.DelStoredChanges();
                
                this._sentchanges = [];
                this._syncchanges = [];
                this._waitchanges = [];
                
                this.toast.ShowAlert('danger', this.lang.tr('@service_commit_failed'), 60000);
                console.warn("[SYNC] Discarded pending commits after repeated application failures");
                return;
            }
        }

        this._syncchanges.push(...this._sentchanges);
        this._sentchanges = [];
    }

    private _ResetFailureState(): void {
        this._httperfailures = 0;
        this._serverretries = this.SERVER_RETRIES;
    }

    /**
     * Exponential backoff with jitter for sync retry delays.
     * Offline transport uses the HTTPER ceiling so we do not hammer while unreachable.
     */
    private _FailureBackoffMs(source: 'HTTPER' | 'SERVER'): number {
        if ((source == 'HTTPER') && !this.sync.http.IsOnline){
            return this.HTTPER_DELAY_MAX_MS;
        }

        const _failures = (source == 'HTTPER') ? this._httperfailures : (this.SERVER_RETRIES - this._serverretries);
        const _base = (source == 'HTTPER') ? this.HTTPER_DELAY_BASE_MS : this.SERVER_DELAY_BASE_MS;
        const _max = (source == 'HTTPER') ? this.HTTPER_DELAY_MAX_MS : this.SERVER_DELAY_MAX_MS;
        const _exp = Math.min(_max, _base * Math.pow(2, Math.max(0, _failures - 1)));
        const _jitter = Math.floor(Math.random() * Math.min(1000, Math.max(1, _exp * 0.1)));
        
        return Math.min(_max, _exp + _jitter);
    }

    /**
     * Subscribes once so an offline or transport delay can end when the link returns.
     */
    private _ArmOnlineWake(): void {
        if (this._onlineWake || !this.sync.http.OnOnline){
            return;
        }

        this._onlineWake = this.sync.http.OnOnline.subscribe((online: boolean) => {
            if (online && this._releaseDelay){
                this._releaseDelay();
            }
        });
    }

    /**
     * Waits up to `ms`, or until connectivity returns, whichever comes first.
     */
    private _WaitOrOnline(ms: number): Promise<void> {
        this._ArmOnlineWake();

        return new Promise((resolve) => {
            let _done = false;
            const _finish = () => {
                if (_done){
                    return;
                }
                _done = true;
                clearTimeout(_timer);
                if (this._releaseDelay == _finish){
                    this._releaseDelay = null;
                }
                resolve();
            };

            const _timer = setTimeout(_finish, ms);
            this._releaseDelay = _finish;
        });
    }

    /**
     * Handles the failure of a synchronization operation.
     * 
     * This method restores sent changes back to the synchronization list
     * and deletes stored changes to prevent inconsistencies.
     */    
    private _prepare_request(): UpdateRequest {
        const _access = this.state.Access;
        if (!_access){
            throw new Error("[SYNC] Access mode has not been initialized!")
        }

        let _target: LoadTarget | null = null;

        if (!_target && this.sync.place){
            _target = 'PLACE';
        }

        if (!_target && this.sync.qrcode){
            _target = 'QRCODE';
        }

        if (!_target && this.sync.session && this.sync.device){
            _target = 'SESSION';
        }

        if (!_target){
            throw new Error("[SYNC] Cannot determine session target!")
        }

        const _getparams: any = {
            access: _access,
            timestamp: this.timestamp(_target),
            target: _target,
        }

        if (this._tableid){
            _getparams['table'] = this._tableid;
        }

        return _getparams;
    }

    /**
     * Resolves dependencies within a single change by replacing UUID references with object IDs.
     * 
     * @param _change - The change to resolve.
     */    
    private _resolve_change(_change: CommitChange){
        if ('_uuid' in _change.relation){
            const _objid = this.resolved.Get(_change.relation['_uuid']);
            if (_objid){
                _change.relation['objid'] = _objid;
            }
        }

        for(const _requires in _change.requires){
            this._resolve_change(_change.requires[_requires]);
        }
    }

    /**
     * Resolves server response updates by mapping UUIDs to object IDs and updating pending changes.
     * 
     * This method processes the server response, resolving any pending changes based on
     * their requirements and removing any that have been fully processed.
     * 
     * @param response - The server response containing updates and metadata.
     */    
    private async _resolve_response(response: UpdateRespose){
        const _updates = response.updates;

        // remove volatile responses from server
        for(let _idx = _updates.length - 1; _idx >= 0; _idx--){
            const _update = _updates[_idx];
            if (('_vltl' in _update) && (_update['_vltl'] == true)){
                _updates.splice(_idx, 1);
            }
        }

        // update the database cache entries
        if (_updates.length > 0){
            console.info("[SYNCHRONIZE] Updating [" + _updates.length + "] entries from server");

            switch(response['target']){
                case 'SESSION':
                    if ((this.sync.user) && (this.sync.user.objid)){
                        await this.cache.SaveSession(_updates);
                    }
                    break;
    
                case 'QRCODE':
                    // nothing to do
                    break;    
    
                case 'PLACE':
                    if ((this.sync.place) && (this.sync.place.objid)){
                        await this.cache.SavePlace(this.sync.place.objid, _updates);
                    }
                    break;
            }    
        }

        // keep the resolved items in the _uuid list
        for (const _update of _updates){
            if (('_actn' in _update) && (_update['_actn'] == 'do_insert')){
                this.resolved.Set(_update['_uuid'], _update['objid']);
            }            
        }   
        
        // Resolve staged wait packages against this response (insert→update, skip conflicts).
        for(const _change of this._waitchanges){
            if ((_change.entrynfo['_actn'] == "do_insert")) {
                if (!('_uuid' in _change.entrynfo)){
                    throw new Error("[SYNC] invalid change information in entry to insert (uuid is not provided)")
                }

                const _objid = this.resolved.Get(_change.entrynfo['_uuid']);
                if (_objid){
                    _change.entrynfo['objid'] = _objid;
                    _change.entrynfo['_actn'] = "do_update";
                }
            }

            // if requirements have been resolved, add resolved objid
            this._resolve_change(_change);

            // skip update if change is still pending
            for(let _idx = _updates.length - 1; _idx >= 0; _idx--){
                const _update = _updates[_idx];
                if (_change.entrynfo['table'] == _update['table']){
                    let _skip = false;

                    if (('_uuid' in _change.entrynfo) && (_change.entrynfo['_uuid'] == _update['_uuid'])){
                        _skip = true;   // found change by _uuid
                    }

                    if (('objid' in _change.entrynfo) && (_change.entrynfo['objid'] == _update['objid'])){
                        _skip = true;   // found change by objid
                    }

                    if (_skip){
                        _updates.splice(_idx, 1);
                    }
                }
            }
        }
    }

    /**
     * Executes a data refresh, sending updates to the server and retrieving changes.
     * @param reset - Whether to reset the state before refreshing.
     * @param forced - Whether to force a refresh, bypassing normal conditions.
     * @returns A promise resolving to `true` if successful, `false` otherwise.
     */    
    async DoRefresh(reset: boolean, forced: boolean): Promise <boolean> {
        let _success = false;

        if (this._unloading){
            return false;
        }

        const _connected = (this.connection.status == 'RUNNING');
        if (this._onrefresh){
            return false;   // skip this refresh (one is ongoing)
        }

        if (!_connected){
            console.info("[SYNC] Not connected: Skiping refresh operation");
            // Promote recovered wait packages so they are not stranded while disconnected.
            this._PromoteWaitChanges();

            // delay next refresh (1 secs) - wait for connection with server
            if (!this._unloading){
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            return false;
        }

        // Offline mode: keep the queue quiet — do not hammer / log request errors.
        // Forced refresh may still attempt once (explicit user/recovery path).
        // Promote wait here too (outside try/finally): this path never enters the
        // refresh cycle that would otherwise fold wait into the send queue.
        if (!forced && !this.sync.http.IsOnline){
            console.info("[SYNC] Offline: Skipping refresh operation");
            this._PromoteWaitChanges();
            if (!this._unloading){
                await this._WaitOrOnline(this.HTTPER_DELAY_MAX_MS);
            }
            // Still offline: keep the queue quiet. Back online: send on this same cycle.
            if (this._unloading || !this.sync.http.IsOnline){
                return false;
            }
        }

        try {
            let data: UpdateRespose | null = null;

            this._onrefresh = true;
            this._refreshCancel = { abort: null };
            this.staleGuard.ArmWatchdog();

            if (reset){
                this.pending.Reset();

                this._timestamp.set('SESSION', 0);
                this._timestamp.set('PLACE', 0);
                this._timestamp.set('QRCODE', 0);
            }

            const _commitsize = this._prepare_syncronize();
            let _httperBackoff = false;
            try {
                const _reqtimeout = (_commitsize > 0) ? Syncronizator._SYNC_WRITE_TIMEOUT_MS : null;
                data = await this.adhoc.DoRequest(this._doRefreshUrl, this._prepare_request(), this._sentchanges, false, null, null, null, _reqtimeout, this._refreshCancel);
            }
            catch(error: any){
                const _aborted = this._clientAbortRefresh;
                this._clientAbortRefresh = false;

                if (_aborted){  // Intentional cancel (flush / wake / watchdog): requeue sent without transport backoff.
                    this._syncchanges.push(...this._sentchanges);
                    this._sentchanges = [];

                    console.info("[REQUEST] Sync request aborted (requeue pending)");
                }
                else {
                    this._onfailure_synchronize('HTTPER');

                    if (error && error.status) {
                        console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._doRefreshUrl + "'");
                    } 
                    else {
                        console.error("[REQUEST] Generic error in http request '" + this._doRefreshUrl + "' (" + error + ")");
                    }

                    // Defer backoff until after DisarmWatchdog — the watchdog must only
                    // cover the in-flight XHR, not intentional retry sleeps (SERVER/HTTPER).
                    _httperBackoff = true;
                }
            }

            // Request finished (resolved or rejected): stop the zombie detector before any backoff.
            this.staleGuard.DisarmWatchdog();

            if (_httperBackoff && !this._unloading){
                // Backoff with ceiling + jitter; offline uses the HTTPER max delay.
                // Coming back online ends the wait so the next cycle can send.
                await this._WaitOrOnline(this._FailureBackoffMs('HTTPER'));
            }

            if (data && !this._unloading){
                this._httperfailures = 0;
                this._onsuccess_syncronize(data['updated']);

                _success = (data.errorcode == 0);
                if (_success){
                    // notify that changes has been commited to server
                    if (_commitsize > 0){
                        console.info("[SYNCHRONIZE] Writing [" + _commitsize + "] entries to server");

                        this.OnRefreshCompleted.pipe(take(1)).subscribe(
                        () => {
                            this._onCommitCompleted.next();
                        });
                    }

                    // update the waitchanges array with the server entries
                    await this._resolve_response(data);

                    this._timestamp.set(data.target, data.timestamp);
                    this._reference = data.reference;

                    this.serverreference = performance.now();    

                    if (this._pending){     // is destroyed??
                        try {
                            await this._pending.Concat(data['updates'], forced);
                        }
                        catch (error) {
                            console.error(error);
                        }
                    }

                    if (data['expired'] && !this._unloading){
                        console.warn("[SERVICE] Client connection lost or expired: Reconnecting");
                        if (await this.connection.RefreshConnection()){
                            console.info("[SERVICE] Client connection succesfully restored");
                        }
                        else {
                            console.error("[SERVICE] Could not restore client connection. Refresh operations may fail!");
                        };
                    }                    
                }
                else {
                    if (!data.updated){
                        this._onfailure_synchronize('SERVER');
                    }

                    if (data.errorcode == 2){
                        this.sync.OnSessionExpired();
                    }
                    else {
                        console.error("[SERVICE] Error [" +  data['errorcode'] + "] in '" + this._doRefreshUrl + "'");
                        this.toast.ShowAlert('danger', this.lang.tr('@service_request_error_' + data['errorcode']));
                    }

                    // Backoff with ceiling + jitter for application errors.
                    await new Promise(resolve => setTimeout(resolve, this._FailureBackoffMs('SERVER')));
                }
            }
        }
        finally {
            this.staleGuard.DisarmWatchdog();
            this._refreshCancel = null;
            this._clientAbortRefresh = false;

            this._PromoteWaitChanges(); // Always promote wait→sync when leaving a refresh cycle (success, HTTP error, or throw after a concurrent FlushChanges staged into wait).
            this._onrefresh = false;
            this._onRefreshCompleted.next();
        }

        return _success;
    }

    /**
     * Cancels an ongoing refresh operation.
     * Aborts the client XHR first, then asks the server to end the long-poll.
     */    
    async CancelRefresh(): Promise <void> {
        if (!this._onrefresh){
            return;     // no refresh in progress
        }

        // Mark before abort so DoRefresh catch does not count intentional cancel as HTTPER.
        this._clientAbortRefresh = true;

        const _cancelled = new Promise <void> ((resolve) => {
            const _wait = performance.now();

            this.OnRefreshCompleted.pipe(take(1)).subscribe(
            () => {
                console.info("[COMMIT] Ongoing refresh returned in " + (performance.now() - _wait).toFixed(2) + " ms.");
                resolve();
            });

            if (this._refreshCancel?.abort){
                this._refreshCancel.abort();
            }

            void this.connection.CancelRefresh();
        })

        await _cancelled;
    }

    /**
     * Forces a refresh operation regardless of the current state.
     *
     * @returns `true` when `/sync` completes with `errorcode == 0`; otherwise `false`.
     */    
    async ForceRefresh(): Promise <boolean> {
        this._ResetFailureState();

        if (this._onrefresh){
            await firstValueFrom(this.OnRefreshCompleted);
        }

        return await this.DoRefresh(false, true);                
    }

    /**
     * Cancels the current refresh and resets the pending queue
     */      
    async ResetSynchronization(){
        await this.CancelRefresh();
        this.pending.Reset();
    }
}

/**
 * Provides synchronization services between the client and the server.
 * 
 * The `syncService` class manages user sessions, device identification, and 
 * data synchronization using a `Syncronizator`. It also handles session expiration 
 * and provides observable streams for external components to react to changes.
 */
@Injectable({
    providedIn: 'root'
})
export class syncService implements OnDestroy {
    private _pageLifecycleNotified = false;

    /**
     * Shared handler for page close/reload. Registered on both `pagehide`
     * (Chrome Permissions Policy) and `unload` (Safari/Firefox fallback).
     * Dedupes so a browser that fires both does not notify twice.
     */
    private _pageLifecycleHandler = () => {
        this._NotifyUnload();
    };

    private _onUnloadHandler: (() => void) | null = null;
    private _onReleased = new Subject <void> ();
    /** Observable that emits when the session is fully released. */
    OnReleased = this._onReleased.asObservable();

    private _onExpired = new Subject <void> ();
    /** Observable that emits when the session expires. */
    public OnExpired = this._onExpired.asObservable();

    private _isReady = new ReplaySubject<boolean>(1);
    /** Observable that emits whenever the service is ready */
    public IsReady = this._isReady.asObservable();

    /**
     * Optional live-store probe: true when `table@_uuid` is mid-commit (`InCommit`).
     * Wired by `dataService` so CheckChanges matches `data.Commit` without circular DI.
     */
    IsInCommit: ((table: string, uuid: string) => boolean) | null = null;

    /**
     * Creates a new instance of the synchronization service.
     * 
     * @param lang - The language service for translations.
     * @param cache - The cache service to avoid server complete reload
     * @param adhoc - The ad-hoc request service for API calls.
     * @param state - The state service for session and device data.
     * @param clock - The clock service for scheduling tasks.
     * @param toast - The toast notification service.
     * @param alertCtrl - The alert controller for displaying modals.
     * @param connect - Facade over the local `pispas-connect` transports (dual-write store + activity logger).
     */    
    constructor(public lang: languageService, public cache: cacheService, public adhoc: adhocService, public http: httpService, public state: stateService, public clock: clockService, public toast: toastService, private deviceid: identificationService, private alertCtrl: alertService, public connect: wsconnectService){
        window.addEventListener('pagehide', this._pageLifecycleHandler);    // pagehide: allowed under Chrome Permissions Policy
        window.addEventListener('unload', this._pageLifecycleHandler);      // unload: Safari/Firefox fallback

        // load the device id only if it is a known device (otherwise delefate to the login process)
        void (async () => {
            try {
                if ((this.state.Access == 'GUEST') || (await this.deviceid.IsKnown())){
                    this.state.device = await this.deviceid.DeviceId();
                    console.info("[DEVICE ID] " + this.state.device);
                }
            }
            catch (error) {
                console.error("[SYNC] Could not load device id", error);
            }
            finally {
                this._isReady.next(true);
                this._isReady.complete();
            }
        })();
    }

    /**
     * Cleans up resources and stops the synchronizer when the service is destroyed.
     */    
    ngOnDestroy(){
        window.removeEventListener('pagehide', this._pageLifecycleHandler);
        window.removeEventListener('unload', this._pageLifecycleHandler);

        if (this._syncronizer){
            this._syncronizer.OnDestroy();
            this._syncronizer = null;
        }
    }   

    /**
     * Callback executed on browser unload when beacon cannot be used.
     */
    set UnloadHandler(handler: (() => void) | null) {
        this._onUnloadHandler = handler;
    }

    /**
     * Notifies backend when browser is closing/reloading.
     * Uses sendBeacon to avoid being cancelled during unload.
     * Idempotent: pagehide + unload may both fire in the same teardown.
     */
    private _NotifyUnload(): void {
        if (this._pageLifecycleNotified || !this.session){
            return;
        }
        this._pageLifecycleNotified = true;

        console.warn("-- UNLOADING PAGE --");

        // Halt sync before unload beacon: an in-flight wait must not recreate
        // CONNECTED and revive SESSION DE→AC after release (tab close).
        // Window refresh still reuses the session via validate_session RefreshTime.
        if (this._syncronizer){
            this._syncronizer.OnUnload();
        }
        
        let _beacon = false;
        if (this.http.IsOnline){
            // Same guest bind as /sync?table= (QRSCAN.objid). Staff unload uses place.
            const _payload = JSON.stringify({
                session: this.session,
                place: this.place ? this.place.objid : null,
                table: this.qrscan ? this.qrscan.objid : null
            });
    
            _beacon = navigator.sendBeacon(AppConstants.baseURL + 'connection/unload', _payload);
        }

        // execute the callback if beacon cannot be used
        if (!_beacon && this._onUnloadHandler) { 
            this._onUnloadHandler();
        }
    }

    /**
     * Gets the current session identifier.
     */    
    get session(): string | null {
        return this.state.session;
    }

    /**
     * Gets the current device identifier.
     */    
    get device(): string | null {
        return this.state.device;
    }

    private _syncronizer: Syncronizator | null = null;

    /**
     * Gets the synchronizer instance.
     * The instance is lazily created if it does not already exist.
     */    
    get synchonizer(): Syncronizator {
        if (!this._syncronizer){
            this._syncronizer = new Syncronizator(this);
        }
        return this._syncronizer;
    } 

    /**
     * Exposes the `OnChange` observable from the synchronizer.
     */    
    get OnChange(): Observable <any> {
        return this.synchonizer.OnChange;
    }

    /***************************************/
    /* START/STOP SESSION                  */
    /***************************************/

    /**
     * Starts the synchronization service.
     * 
     * Initializes the device ID, sets up the synchronizer, and establishes a session.
     * 
     * @param session - The session ID to start.
     * @returns A promise resolving to `true` if the operation is successful, otherwise `false`.
     * @throws If the device ID cannot be established.
     */    
    async Start(session: string | null) : Promise <boolean> {
        if (this.state.device){
            console.info("[SYNC] Device id is '" + this.state.device+ "'");

            const _result  = await this.synchonizer.Start(session);
            if (!_result){
                this.state.session = null;
            }
            return _result;
        }
        else {
            throw new Error("[SYNC] Device id has not been set!");
        }
    }

    /**
     * Stops the synchronization service.
     * 
     * Closes the connection, clears the session, and stops the synchronizer.
     */    
    async Stop() : Promise <void> {
        console.info("[FINALIZE] Closing data service connection");
        
        await this.synchonizer.Stop();
        
        this.state.session = null;
        this._onReleased.next();
        
        console.info("[FINALIZE] Syncronization is stopped");
    }

    /***************************************/
    /* ENTRY POINT                         */
    /***************************************/

    private _user: User | null = null;

    /**
     * Gets the currently associated user.
     */    
    get user() : User | null {
        return this._user;
    }

    /**
     * Updates the user associated with the synchronization service.
     * 
     * @param user - The user to associate, or `null` to disassociate the user.
     * @returns A promise resolving to `true` if the operation is successful, otherwise `false`.
     */    
    async SetUser(user: User | null): Promise <boolean> {
        if (this._user == user){
            return true;    // no changes
        }

        const _result = await this.synchonizer.SetUser(user);
        if (_result){
            this._user = user;
        }

        return _result;
    }

    private _qrscan: QrScan | null = null;

    /**
     * Gets the currently associated QR scan (guest table bind).
     */
    get qrscan() : QrScan | null {
        return this._qrscan;
    }

    /**
     * Updates the QR scan associated with the synchronization service.
     *
     * Binds guest `/sync ?table=` to `QRSCAN.objid` before the tenant QRCODE row is loaded.
     *
     * @param qrscan - The scan to associate, or `null` to clear it.
     * @returns A promise resolving to `true` if the operation is successful, otherwise `false`.
     */
    async SetQrScan(qrscan: QrScan | null): Promise <boolean> {
        if (this._qrscan == qrscan){
            return true;
        }

        const _result = await this.synchonizer.SetQrScan(qrscan);
        if (_result){
            this._qrscan = qrscan;
        }

        return _result;
    }

    private _qrcode: QrCode | null = null;

    /**
     * Gets the currently associated QR code.
     */    
    get qrcode() : QrCode | null {
        return this._qrcode;
    }

    /**
     * Updates the QR code associated with the synchronization service.
     * 
     * @param qrcode - The QR code to associate, or `null` to disassociate the QR code.
     * @returns A promise resolving to `true` if the operation is successful, otherwise `false`.
     */    
    async SetQrCode(qrcode: QrCode | null): Promise <boolean> {
        if (this._qrcode == qrcode){
            return true;    // no changes
        }

        const _result = await this.synchonizer.SetQrCode(qrcode);
        if (_result){
            this._qrcode = qrcode;
        }
        
        return _result;
    }

    private _place: Place | null = null;

    /**
     * Gets the currently associated place.
     */    
    get place() : Place | null {
        return this._place;
    }

    /**
     * Updates the place associated with the synchronization service.
     * 
     * @param place - The place to associate, or `null` to disassociate the place.
     * @returns A promise resolving to `true` if the operation is successful, otherwise `false`.
     */    
    async SetPlace(place: Place | null): Promise <boolean> {
        if (this._place == place){
            return true;    // no changes
        }

        const _result = await this.synchonizer.SetPlace(place);
        if (_result){
            this._place = place;
        }

        return _result;
    }

    /***************************************/
    /* SYNCHRONIZATION CONTROL              */
    /***************************************/

    /**
     * Resets the synchronization stage to its initial state.
     * 
     * @returns A promise resolving when the reset is complete.
     */
    async ResetStage(): Promise <void> {
        await this.synchonizer.ResetSynchronization();
    }

    /**
     * Flushes changes to the server.
     * 
     * @param changes - The list of changes to flush.
     * @param force - Whether to force the flush immediately.
     * @returns A promise resolving to `true` if the operation is successful, otherwise `false`.
     */    
    async Flush(changes: CommitChange[], force = false) : Promise <boolean> {
        return await this.synchonizer.FlushChanges(changes, force);
    }

    /*********************************/
    /* EXPIRATION MODAL              */
    /*********************************/

    private _expired_notified = false;

    /**
     * Handles the expiration of a user session.
     *
     * Stops the synchronizer, optionally notifies the user via a modal alert, and emits an expiration event.
     * @param showModal - If true, shows the session-expired modal. Set to false when expiration happens
     *   during restore (e.g. stored login with expired session) so the user is not interrupted.
     */
    async OnSessionExpired(showModal = true){
        if (this._expired_notified){
            return;
        }

        if (this.synchonizer.status != 'EXPIRED'){
            this.synchonizer.status = 'EXPIRED';

            this._expired_notified = true;
            await this.synchonizer.Stop();

            if (!showModal){
                this._expired_notified = false;
                this._onExpired.next();
                return;
            }

            // Ensure translations are available before building a one-shot modal.
            await this.lang.WaitReady('base');

            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@user_session_expired_title'),
                message: this.lang.tr('@user_session_expired_message'),
                backdropDismiss: true,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
        
                        return false;
                    }
                }]
            });
            
            alert.onDidDismiss().then(
            () => {
                this._expired_notified = false;    
                this._onExpired.next();
            });

            await alert.present();
        }
    }
}